/* ============================================
   QUOTE DASHBOARD — Lead Detail Card (Form Submission / CFA Handoff)
   Asset: dashboard-lead-detail.js

   Extracted from dashboard-main.js on 2026-05-23 as Phase B.2.b of the
   dashboard refactor — the `if (qIsLead)` branch of buildDetailHtml.
   Renders the full lead-state detail card (NEW LEAD / SAMPLE / DEAD /
   ON HOLD rows) including:
   Section order, top to bottom (Ryan 2026-08-20 — Samples moved up):
     - Form Submission / CFA Handoff heading
     - Samples bullet list + "Request" button (sample-request leads)
     - CFA Handoff Notes + BANT discovery snapshot (CFA leads)
     - Dead-lead banner + Resurrect CTA (dead rows)
     - Shipment Tracking summary + hidden edit row (admin, on sample rows w/ data)
     - Follow-up emails ladder row (admin)
     (the ON HOLD banner was removed 2026-08-20 — redundant with the Deal Feed entry
      /lead-hold writes, and with "⏵ Remove Hold" on the row)
     - Customer Message / Initial Conversation Notes / Source Context body
     - Pages Viewed, Form Responses, Attachments

   ⛔ SHIPMENT TRACKING IS A SUMMARY THAT HIDES A FORM, not a form. Its three inputs are
   still in the DOM, still carrying the class names handleSampleTrackingSave queries off
   the wrapper — `.rqd-sample-tracking--summary` just hides them until a value is clicked.
   Anything that changes those class names, or moves the inputs out of the wrapper, breaks
   saving from a card that LOOKS like read-only text.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildLeadDetail(q) -> html string
       Returns the entire <div class="rqd-detail__card rqd-detail__lead">
       ...</div> block. Caller (buildDetailHtml inside main.js) just
       concatenates the result into its own html accumulator.

   Init contract (RQD.initLeadDetail called once by main.js at boot):
     esc, formatDate, fmtPhone — function declarations in main, safe to
       pass by reference per the init-deps-var-hoisting-trap memo.
     getIsAdmin — closure-getter, NOT a bare reference. isAdmin is a `var`
       in main that mutates after Auth.getSession() resolves (~L91), so a
       bare reference captures undefined/false at init time and the sample-
       tracking admin block silently drops out. Mirrors the pattern used
       by initShippingCards / initOrderCards.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initLeadDetail (from main.js boot)
  var esc, formatDate, fmtPhone, getIsAdmin;

  /* One row of the Shipment Tracking summary: a label cell and a click-to-edit value cell.
     ⚠️ MODULE SCOPE, not a local inside buildLeadDetail(). A helper declared inside the render
     function is rebuilt on every row draw, and — the worse half — it reads exactly like a shared
     module helper to anyone scanning the file, which is how a call from a second function ends up
     throwing ReferenceError (see the `_e` bug, 2026-08-19 PM¹¹). `value` and `placeholder` arrive
     ALREADY HTML-escaped by the caller; this does not escape them again. */
  function _viewRow(label, field, value, placeholder, isUrl) {
    var v = String(value || '');
    var cell;
    if (v) {
      cell = '<span class="rqd-sample-tracking__text">' + v + '</span>'
        /* ⚠️ The URL's OPEN affordance is a separate hit target, deliberately. One element that
           both navigates and enters edit mode on the same click is a coin toss; the arrow opens,
           the text edits, and the two never have to guess. */
        + (isUrl ? '<a class="rqd-sample-tracking__open" href="' + v + '" target="_blank" rel="noopener"'
                 + ' title="Open the tracking page in a new tab">↗</a>' : '');
    } else {
      cell = '<span class="rqd-sample-tracking__empty">' + placeholder + '</span>';
    }
    return '<div class="rqd-sample-tracking__k">' + label + '</div>'
      + '<div class="rqd-sample-tracking__v" data-field="' + field + '" role="button" tabindex="0"'
      + ' title="Click to edit">' + cell + '</div>';
  }

  /* ============================================
     THE PROJECT BEHIND THE BOX — type + square feet, and the gate on Request
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06)
     ============================================
     Ryan: *"sometimes people will bypass this form and call us up to order their samples… we need
     to have an area where project type and square feet of project can live whether this was
     pulled in from an initial inquiry or filled out after the fact… The 'Request' button needs to
     be gated… grayed out/not clickable if the 'Adjust' list hasn't been compiled first… also
     gated by these 2 fields being filled out."*

     ⛔ DECLARED HERE, MODULE SCOPE, AND EXPORTED — for the same reason `samplesListFor` is. The
     quote-owned card (dashboard-sample-commission.js) draws the identical row and the identical
     gate, and the picker (dashboard-sample-picker.js) re-evaluates the gate after a save without a
     re-render. Three callers, one rule: "is this deal ready to ask ECORE" must have exactly one
     answer on every surface, or the button on one card says Request while the other says why not.

     ⛔ THE LIST IS THE WEBSITE FORM'S, VERBATIM (sections/custom-ecore-sample-picker.liquid), and
     the worker keeps a third copy in lib/sample-project.js for its own readers. Change one, change
     all three. "Other" reveals a free-text box and is stored as "Other — <text>", the exact shape
     the storefront writes, so the two writers cannot be told apart downstream.

     ⚠️ THE GATE IS PURE AND TAKES A SHAPE, NOT A ROW. At render time it is fed from `q`; after a
     save the picker feeds it from the DOM (the list count it just painted, the values in the row's
     own inputs) because the row in `allQuotes` may not have been told yet. Two feeders, one rule.

     ⛔ DISABLED, NOT MERELY GHOSTED — the opposite of the "sent" state, on purpose. Ghosting says
     "already done, but you may again"; this says "cannot yet". The two must not look alike, which
     is why `--blocked` carries a `not-allowed` cursor and the sent state is forbidden one (see the
     button-state test). And a disabled button with no explanation is a gate with no step, so the
     hint line under the buttons names what is missing, in the same words the worker's 409 uses. */
  var SAMPLE_PROJECT_TYPES = [
    'K-12 School / Higher Ed',
    'Police & Fire',
    'Multifamily / Apartment Community',
    'Hotel / Hospitality',
    'Physical Therapy / Rehab Clinic',
    'Senior Living',
    'Corporate Wellness',
    'Commercial Gym',
    'Yoga / Pilates Studio',
    'Multi-Purpose Room',
    'Home Gym',
    'Outdoor / Equestrian',
    'Other',
  ];
  window.RQD.SAMPLE_PROJECT_TYPES = SAMPLE_PROJECT_TYPES;

  /** `{ ready, missing: [] }` from `{ samplesCount, type, squareFeet, hasAddress }`. */
  function sampleRequestGate(input) {
    var i = input || {};
    var missing = [];
    if (!(Number(i.samplesCount) > 0)) missing.push('a sample list (use Adjust)');
    if (!String(i.type || '').trim()) missing.push('a project type');
    if (!String(i.squareFeet || '').trim()) missing.push('the square footage');
    /* Ryan 2026-09-06: the Shipping Address section replaced the pop-up on Request, so an address
       is a requirement the card shows rather than a question the click asks. `hasAddress` is
       undefined only for a caller that predates the field, which must not start blocking. */
    if (i.hasAddress === false) missing.push('a shipping address');
    return { ready: missing.length === 0, missing: missing };
  }
  window.RQD.sampleRequestGate = sampleRequestGate;

  /* ============================================
     THE SHIPPING ADDRESS FOR THE BOX
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06)
     ============================================
     Ryan: *"a SHIPPING ADDRESS section between the SAMPLES and JOB INFORMATION sections… it can
     just repeat the shipping address from the deal as standard, but include a pencil icon to edit
     it if need-be… a one-time thing, independent of the shipping address on the deal itself. Then
     when 'Request' is clicked, we no longer need the pop-up."* And the case behind it: a customer
     who typed a business name on the form for a box going to their house — *"edit that and remove
     that line while keeping the rest the same."*

     ⛔ THE SAME PRECEDENCE THE EMAIL USES (`shipBlockOf`, worker): the card's own one-time block
     (`q.sampleShipTo`, written by POST /sample-ship-to) → the deal's Ship To → the Sold To. The row
     already carries the deal's addresses, so no fetch is needed to show the default, and the
     worker is still the boundary: compose refuses a deal with no address anywhere.
     ⚠️ Contact, Organization, Street, City / State / ZIP only — no email or phone. Those default
     to Abby's on the work order (the samples desk should ring US, not the customer). */
  function sampleShipToFor(q) {
    if (!q) return { source: 'none' };
    var pick = function (src, a) {
      return { source: src, name: String(a.name || q.customer || ''), company: String(a.company || ''),
        street: String(a.street || ''), street2: String(a.street2 || ''), city: String(a.city || ''),
        state: String(a.state || ''), zip: String(a.zip || ''),
        /* CUSTOM_SAMPLECONTACT_BY_CLAUDE (Ryan 2026-09-23) — where Abby's tracking notice and the follow-ups go when the box ships to
           somebody other than the Sold To; the Sold To is CC'd. Blank means the Sold To, as before. */
        email: String(a.email || '') };
    };
    var has = function (a) { return !!(a && (String(a.street || '').trim() || String(a.city || '').trim() || String(a.zip || '').trim())); };
    if (has(q.sampleShipTo)) return pick('sample', q.sampleShipTo);
    if (has(q.shipTo)) return pick('ship-to', q.shipTo);
    var sold = { name: q.customer, company: q.customerCompany, street: q.customerStreet, street2: q.customerStreet2, city: q.city, state: q.state, zip: q.zip };
    if (has(sold)) return pick('sold-to', sold);
    return Object.assign(pick('none', {}), { name: String(q.customer || '') });
  }
  window.RQD.sampleShipToFor = sampleShipToFor;

  /** The section: heading, then a band — the address grid (or the edit row) left, the pencil right.
      `opts.readOnly` drops the pencil; `opts.sub` marks the heading for the quote-side collapse rule. */
  function sampleShipToRowHtml(q, opts) {
    var o = opts || {};
    var ref = (q && q.reference) || '';
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: with `opts.lane === 2` this
       is the SECOND recipient's own address (q.sampleSecondary.shipTo), never the deal's. Same rows,
       same edit row, same classes, plus `data-lane="2"` on the block and on every button so the
       picker's handlers post `recipient: 2` and the primary selectors can exclude it. No "Use deal
       address" here: a second recipient with the buyer's address is not a second recipient. */
    var _lane2 = o.lane === 2;
    var _b2 = _lane2 ? sampleSecondaryOf(q) : null;
    if (_lane2 && !_b2) return '';
    var a = _lane2 ? _secondaryShipToFor(_b2) : sampleShipToFor(q);
    var hasAddr = a.source !== 'none';
    var editable = !o.readOnly && canEditSampleProject(q);
    var _laneAttr = _lane2 ? ' data-lane="2"' : '';
    var _unsaved2 = !!(_lane2 && _b2._unsaved);
    var cityLine = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    /* CUSTOM_CANADA_BY_CLAUDE (Ryan 2026-09-23) — a Canadian postal code puts a Country row under the
       city line, the same line ECORE's work order prints (worker lib/address-country.js is the rule;
       this is its one theme-side copy, on purpose the same regex). */
    var countryLine = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(String(a.zip || '').trim()) ? 'Canada' : '';
    var EMPTY = '<span class="rqd-sample-tracking__empty">Not recorded yet</span>';
    var row = function (k, v) {
      return '<div class="rqd-sample-tracking__k">' + k + '</div>'
        + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static">' + (v ? '<span class="rqd-sample-tracking__text">' + esc(v) + '</span>' : EMPTY) + '</div>';
    };
    var html = '<div class="rqd-detail__section-title' + (o.sub ? ' rqd-detail__section-title--sub' : '')
      + ' rqd-sample-ship__title" style="margin-top:18px;font-size:13px;">Shipping Address</div>';
    html += '<div class="rqd-sample-band rqd-sample-band--ship">';
    html += '<div class="rqd-sample-ship" data-ref="' + esc(ref) + '"' + _laneAttr + (_unsaved2 ? ' data-unsaved="1"' : '') + ' data-has="' + (hasAddr ? '1' : '0') + '" data-source="' + esc(a.source) + '"' + (editable ? ' data-editable="1"' : '') + '>';
    /* The view: only the lines that exist, the way the work order prints them. */
    html += '<div class="rqd-sample-ship__view rqd-sample-tracking__view"' + (_unsaved2 ? ' hidden' : '') + '>';
    if (!hasAddr) {
      html += row('Address', '');
    } else {
      if (a.name) html += row('Contact', a.name);
      if (a.company) html += row('Organization', a.company);
      html += row('Street', [a.street, a.street2].filter(Boolean).join(', '));
      html += row('City / State / ZIP', cityLine);
      if (countryLine) html += row('Country', countryLine);
      if (a.email) html += row('Email', a.email);
    }
    html += '</div>';
    if (editable) {
      var inp = function (field, ph, val, cls) {
        return '<input type="text" class="rqd-sample-ship__input' + (cls ? ' ' + cls : '') + '" data-ship-field="' + field + '" placeholder="' + ph + '" aria-label="' + ph + '" value="' + esc(val || '') + '">';
      };
      html += '<div class="rqd-sample-ship__row"' + (_unsaved2 ? '' : ' hidden') + '>'
        + inp('name', _lane2 ? 'Contact (required)' : 'Contact', a.name, 'rqd-sample-ship__input--wide')
        + inp('company', 'Organization', a.company, 'rqd-sample-ship__input--wide')
        + inp('street', 'Street', a.street, 'rqd-sample-ship__input--wide')
        + inp('street2', 'Street 2', a.street2)
        + inp('city', 'City', a.city)
        + inp('state', 'ST / Prov', a.state, 'rqd-sample-ship__input--narrow')
        + inp('zip', 'ZIP / Postal code', a.zip, 'rqd-sample-ship__input--narrow')
        /* CUSTOM_SAMPLECONTACT_BY_CLAUDE (Ryan 2026-09-23) — optional. Set it when the box goes to somebody other than the Sold To
           (RW-1431-A: the client's designer): the tracking email and the follow-ups go here, the Sold To
           is CC'd. The ECORE work order never shows it. */
        + inp('email', _lane2 ? 'Email (optional, for the record only; this person gets no emails from us)' : 'Email for tracking + follow-ups (optional, Sold To copied)', a.email, 'rqd-sample-ship__input--wide')
        + '<span class="rqd-sample-ship__controls">'
        + '<button type="button" class="rqd-sample-ship__save rqd-invoice__link" data-ref="' + esc(ref) + '"' + _laneAttr + ' title="' + (_lane2 ? 'Save the second recipient\'s address. Nothing is emailed.' : 'Use this address for these samples only. The deal\'s own Ship To is not changed.') + '">Save</button>'
        + '<button type="button" class="rqd-sample-ship__cancel rqd-invoice__link"' + _laneAttr + ' title="' + (_unsaved2 ? 'Drop the second recipient.' : 'Discard these edits.') + '">Cancel</button>'
        + (!_lane2 && a.source === 'sample' ? '<button type="button" class="rqd-sample-ship__reset rqd-invoice__link" data-ref="' + esc(ref) + '" title="Drop the one-time address and go back to the deal\'s own.">Use deal address</button>' : '')
        + '</span>'
        + '<span class="rqd-sample-ship__status" aria-live="polite"></span>'
        + '</div>';
    }
    html += '</div>';
    if (editable) {
      /* CUSTOM_SHIPEDITSETTLED_BY_CLAUDE (Ryan 2026-09-12) — once the box has SHIPPED this address
         is a record of where it went, not a choice, so the pencil ghosts out.
         ⛔ BOTH COPIES OF THE TRACKING, and that is not belt-and-braces: the comment further down
         this file records that `q.sampleTracking` is the LEAD's copy while a deal that came in as
         an `rfq` keeps its own under `quoteSampleTracking`. Reading one would leave the button
         bright on every deal whose record happens to live on the other side.
         ⚠️ GHOSTED, NOT REMOVED OR DISABLED — same call as the freight card. A shipped box can
         still be the wrong address that somebody needs to correct for the RECORD, and the button
         is also the only route to "Use deal address". The tooltip carries the warning instead. */
      var _shipped = _lane2
        ? !!sampleSecondaryTracking(_b2)
        : !!((q.sampleTracking && q.sampleTracking.number)
        || (q.quoteSampleTracking && q.quoteSampleTracking.number));
      var _editTitle = _shipped
        ? 'These samples have already shipped, so editing this changes the record only — it does not move the box.'
        : (_lane2 ? 'Edit where the second set ships. Nothing is emailed.' : 'Edit where these samples ship. A one-time address for the box; the deal\'s own Ship To is not changed.');
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) - `opts.belowActionsHtml`: markup the caller wants
         stacked directly UNDER the pencil, in its column (the second recipient's Remove + Request pair).
         Ryan: *"we get 2 buttons for 'Remove' and 'Request'.. Can you tighten their positioning to the
         single button above them?"* Before this they were a row of their own after the band, i.e. an
         address's height below the pencil. `--stack` is the column the Job Information band uses. */
      var _below = String(o.belowActionsHtml || '');
      if (!_unsaved2) html += '<div class="rqd-sample-request__actions' + (_below ? ' rqd-sample-request__actions--stack' : '') + '">'
        + '<button type="button" class="rqd-sample-ship__edit rqd-invoice__link'
        + (_shipped ? ' rqd-sample-ship__edit--settled' : '') + '" data-ref="' + esc(ref) + '"' + _laneAttr
        + ' title="' + _editTitle + '">&#9998; Edit</button>'
        + _below
        + '</div>';
    }
    html += '</div>';
    return html;
  }
  window.RQD.sampleShipToRowHtml = sampleShipToRowHtml;
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  /* ============================================
     CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient ("lane 2")
     ============================================
     Ryan: *"architecture firms will ask that we ship samples to them to have on file, and to their
     client… the primary receiver (the buyer) who we want to send the samples to, send tracking to,
     and then send the automation follow-up emails to… The other address we don't need to follow up
     with."* One deal, two boxes, two people. The worker keeps the second recipient as ONE baked
     field on the row, `q.sampleSecondary`, beside the primary chain it never touches:
       { shipTo, samples[], sampleItems[], sampleRequest|null, sampleTracking|null, sampleShipments[],
         createdAt, createdBy, requested, status:'draft'|'requested'|'shipped'|'delivered' }
     ⛔ ABSENT KEY = NO SECOND RECIPIENT (memory: baked-row-absent-key-vs-null). A row baked before the
     deploy has no key at all; a present `null` is the worker saying it looked. Both render nothing.
     ⛔ EVERY CONTROL IN THE BLOCK CARRIES `data-lane="2"` AND EVERY PRIMARY SELECTOR EXCLUDES IT. The
     picker repaints `.rqd-lead-samples[data-ref]`, the gate reads `.rqd-sample-ship[data-ref]`, the
     save handlers find inputs by class inside `.rqd-sample-tracking` — a second copy of each with the
     same ref would be repainted or read by whichever came first in the DOM. The lane attribute is what
     keeps "the buyer's widget" and "the second recipient's widget" two different questions.
     ⚠️ This person gets NO email from us (Ryan 2026-09-24): no Notify, no Customer notice row, no
     Follow-up row in the block. The buyer's one notice names both tracking numbers, and the buyer's
     notice WAITS for this box (see `_noticePaint` and the Notify button's "Awaiting Box 2"). */
  function sampleSecondaryOf(q) {
    if (!q || !Object.prototype.hasOwnProperty.call(q, 'sampleSecondary')) return null;
    var b = q.sampleSecondary;
    if (!b || typeof b !== 'object' || b.removedAt) return null;
    return b;
  }
  function sampleSecondaryRequested(b) {
    return !!(b && (b.requested || (b.sampleRequest && (b.sampleRequest.sentAt || b.sampleRequest.scheduledFor))));
  }
  function sampleSecondaryTracking(b) {
    return (b && b.sampleTracking && b.sampleTracking.number) ? b.sampleTracking : null;
  }
  function sampleSecondaryStatus(b) {
    if (!b) return 'draft';
    var t = sampleSecondaryTracking(b);
    if (t && t.delivery && (t.delivery.deliveredAt || t.delivery.status === 'DL')) return 'delivered';
    if (t) return 'shipped';
    if (sampleSecondaryRequested(b)) return 'requested';
    if (b.status === 'requested' || b.status === 'shipped' || b.status === 'delivered') return String(b.status);
    return 'draft';
  }
  /** The buyer's notice is waiting: the second set was requested and has no tracking number yet. */
  function samplePairWaiting(q) {
    var b = sampleSecondaryOf(q);
    return !!(b && sampleSecondaryRequested(b) && !sampleSecondaryTracking(b));
  }
  /** What the Notify modal hands the worker so the buyer's email names the second set too. */
  function samplePairFor(q) {
    var b = sampleSecondaryOf(q);
    var t = sampleSecondaryTracking(b);
    if (!b || !t) return null;
    return {
      tracking: { number: String(t.number || ''), carrier: String(t.carrier || ''), url: String(t.url || '') },
      name: String((b.shipTo && b.shipTo.name) || ''),
      company: String((b.shipTo && b.shipTo.company) || ''),
    };
  }
  function _secondaryShipToFor(b) {
    var s = (b && b.shipTo) || {};
    var has = !!(String(s.street || '').trim() || String(s.city || '').trim() || String(s.zip || '').trim());
    return { source: has ? 'second' : 'none', name: String(s.name || ''), company: String(s.company || ''),
      street: String(s.street || ''), street2: String(s.street2 || ''), city: String(s.city || ''),
      state: String(s.state || ''), zip: String(s.zip || ''), email: String(s.email || '') };
  }
  window.RQD.sampleSecondaryOf = sampleSecondaryOf;
  window.RQD.sampleSecondaryStatus = sampleSecondaryStatus;
  window.RQD.samplePairWaiting = samplePairWaiting;
  window.RQD.samplePairFor = samplePairFor;

  /** THE BUTTON. Ryan 2026-09-24: *"make it the same color as the 'Request' button when it hasn't
      been clicked on yet.. Position it just under the 'Request' button, and make it the same size"*
      and *"make it '+2nd Recipient'"*. So it is no longer a row of its own after the Job Information
      band (which put it 8px under the BAND, i.e. a grid's height below Request): `sampleProjectRowHtml`
      draws it INSIDE the band's right-hand column, directly under Request, in Request's box and accent
      (the CSS selector list Request is on). Always rendered for an editor; `--off` hides it while a
      block exists, and the repaint below flips that class rather than re-drawing the band. */
  function sampleSecondaryAddBtnHtml(q) {
    if (!q || !canEditSampleProject(q)) return '';
    var _ref = esc(q.reference || '');
    return '<button type="button" class="rqd-sample-second__add-btn rqd-invoice__link' + (sampleSecondaryOf(q) ? ' rqd-sample-second__add-btn--off' : '') + '" data-ref="' + _ref + '"'
      + ' title="Ship a second set of these samples to somebody else (an architect\'s client, a job site). Its own address, its own ECORE request, its own tracking number. That person gets no emails from us.">+2nd Recipient</button>';
  }
  window.RQD.sampleSecondaryAddBtnHtml = sampleSecondaryAddBtnHtml;

  /** THE BLOCK. Both cards call it after the Job Information band and before Shipment Tracking.
      `opts.sub` marks the headings `--sub` (the quote-side card's collapse rule). It always returns a
      `__slot` keyed on the ref - empty when there is no block - so the repaint has a fixed place to put
      the block when "+2nd Recipient" (in the Job Information band, above) creates one. */
  function sampleSecondaryBandHtml(q, opts) {
    var o = opts || {};
    if (!q) return '';
    var ref = q.reference || '';
    var _qsRefEsc = esc(ref);
    var b = sampleSecondaryOf(q);
    /* ⛔ USED BY THE BLOCK BODY BELOW (Adjust, Remove). Dropped by mistake when the head of this function
       was rewritten on 2026-09-24 - the first click on "+2nd Recipient" then threw a ReferenceError inside
       the repaint and the button "did nothing". The offline harness in the changelog entry catches it. */
    var editable = canEditSampleProject(q);
    var subCls = o.sub ? ' rqd-detail__section-title--sub' : '';
    var subFlag = o.sub ? '1' : '0';
    var slotOpen = '<div class="rqd-sample-second__slot" data-ref="' + _qsRefEsc + '" data-sub="' + subFlag + '">';
    if (!b) return slotOpen + '</div>';
    var status = sampleSecondaryStatus(b);
    var requested = sampleSecondaryRequested(b);
    var trk = sampleSecondaryTracking(b);
    var unsaved = !!b._unsaved;
    var list = (Array.isArray(b.samples) ? b.samples : []).filter(function (s) { return s && String(s).trim(); });
    var chipCls = status === 'delivered' ? 'rqd-sample-tag--delivered'
      : status === 'shipped' ? 'rqd-sample-tag--shipped'
      : status === 'requested' ? 'rqd-sample-tag--requested' : 'rqd-sample-tag--new';
    var chipText = status === 'delivered' ? 'Delivered' : status === 'shipped' ? 'Shipped'
      : status === 'requested' ? 'Requested' : (unsaved ? 'Unsaved' : 'Draft');
    var who = String((b.shipTo && b.shipTo.name) || '').trim();
    var html = '<div class="rqd-sample-second" data-ref="' + _qsRefEsc + '" data-lane="2" data-status="' + esc(status) + '" data-sub="' + subFlag + '">';
    html += '<div class="rqd-detail__section-title' + subCls + ' rqd-sample-second__title" style="margin-top:18px;font-size:13px;">'
      + '<span>Second Recipient' + (who ? ' <span class="rqd-sample-second__who">' + esc(who) + '</span>' : '') + '</span>'
      + '<span class="rqd-sample-tag rqd-sample-second__chip ' + chipCls + '" title="' + esc(
          status === 'delivered' ? 'The second set was delivered.'
          : status === 'shipped' ? 'The second set is on its way.'
          : status === 'requested' ? 'ECORE has been asked to ship the second set. Waiting on a tracking number.'
          : unsaved ? 'Save the address to create the second recipient.' : 'Not yet requested from ECORE.') + '">' + chipText + '</span>'
      + '</div>';
    html += '<div class="rqd-sample-tracking__meta rqd-sample-second__note">Gets no emails from us; the buyer\'s tracking notice names both sets.</div>';
    /* The list: the second recipient's own, through the one renderer (lane 2 = plain lines, no
       first-box tags — those belong to the buyer's chain). */
    var adjust = (editable && !unsaved)
      ? '<button type="button" class="rqd-sample-adjust' + (requested ? ' rqd-sample-adjust--sent' : '') + ' rqd-invoice__link" data-ref="' + _qsRefEsc + '" data-lane="2"'
        + ' title="Change which samples ship to the second recipient. Updates this list and the request email with it.">' + (list.length ? 'Adjust' : 'Add') + '</button>'
      : '';
    html += '<div class="rqd-sample-band rqd-sample-band--samples rqd-sample-band--second">'
      + '<div class="rqd-lead-samples" data-ref="' + _qsRefEsc + '" data-lane="2" data-count="' + list.length + '">' + sampleListHtml(q, list, { lane: 2 }) + '</div>'
      + (adjust ? '<div class="rqd-sample-request__actions">' + adjust + '</div>' : '')
      + '</div>';
    if (unsaved) html += '<div class="rqd-sample-tracking__meta rqd-sample-second__note">Starts as a copy of the buyer\'s list. Save the address first, then Adjust it.</div>';
    /* CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE (Ryan 2026-10-03) — the second recipient's own box (Ryan: "own checkbox"). */
    if (!unsaved) html += '<div class="rqd-sample-tracking__view rqd-sample-overnight-grid">' + sampleOvernightCellsHtml(q, 2, editable && !requested) + '</div>';
    /* The address: the same section the buyer has, keyed on lane 2. */
    /* Ryan 2026-09-24: the address section is drawn AFTER the pair below is built, because the pair now
       rides in that section's Edit column (`belowActionsHtml`) instead of a row of its own under it. */
    var _secondActions = '';
    /* Request + Remove. The gate: a list, an address, and the FIRST request already gone (a second
       address before that is a correction of the first, not a second recipient — the worker refuses
       it the same way). */
    if (!unsaved) {
      var missing = [];
      if (!list.length) missing.push('a sample list (use Add)');
      if (_secondaryShipToFor(b).source === 'none') missing.push('the second recipient\'s address');
      var pReq = (typeof sampleRequestFor === 'function') ? sampleRequestFor(q) : (q.sampleRequest || null);
      if (!(pReq && (pReq.sentAt || pReq.scheduledFor))) missing.push('the first request to have gone');
      var ready = missing.length === 0;
      var reqBtn = getIsAdmin()
        ? '<button type="button" class="rqd-sample-request rqd-invoice__link'
          + (requested ? ' rqd-sample-request--sent' : '')
          + (requested && !(b.sampleRequest && b.sampleRequest.sentAt) ? ' rqd-sample-request--scheduled' : '')
          + (ready || requested ? '' : ' rqd-sample-request--blocked') + '" data-ref="' + _qsRefEsc + '" data-lane="2"'
          + (ready || requested ? '' : ' disabled')
          + ' title="' + esc(!ready && !requested
              ? 'Not ready to send: needs ' + missing.join(', ') + '.'
              : requested
              ? 'Already requested' + (b.sampleRequest && b.sampleRequest.sentAt ? ' on ' + (formatDate(b.sampleRequest.sentAt) || '') : '') + '. Click again only if they lost it — the composer will ask you to confirm.'
              : 'Email ECORE\'s samples desk as Abby, asking them to ship this set to the second recipient. A separate work order under this person\'s name. Opens the composer so you can read it first.')
          + '">' + (requested ? ((b.sampleRequest && b.sampleRequest.sentAt) ? 'Sent' : '⌛ Scheduled') : 'Request') + '</button>'
        : '';
      var rmBtn = (editable && !requested)
        ? '<button type="button" class="rqd-sample-second__remove rqd-invoice__link" data-ref="' + _qsRefEsc + '" data-lane="2" title="Drop the second recipient. Nothing has been requested for them.">Remove</button>'
        : '';
      if (reqBtn || rmBtn) _secondActions = '<div class="rqd-sample-request__actions rqd-sample-second__actions">' + rmBtn + reqBtn + '</div>';
    }
    html += sampleShipToRowHtml(q, { sub: o.sub, lane: 2, belowActionsHtml: _secondActions });
    /* Tracking: its own widget, same classes as the buyer's (the save handler finds the inputs by
       class inside the wrapper and reads `data-lane` to post `recipient: 2`), rendered once the request
       has gone or a number exists. No Notify, no ladder rows.
       ⛔ ALWAYS `--sub` on this heading — inside the quote-side card a bare section title is exempt
       from the collapse rule, and inside the lead card `--sub` is what the "Shipment N" captions wear. */
    if (getIsAdmin() && !unsaved && (requested || trk)) {
      var stOwner = ((Array.isArray(q.leadSamples) && q.leadSamples.length) || String(q.leadCaptureSource || '') === 'sample-request') ? 'lead' : 'wm';
      var st = trk || {};
      var stCarrier = st.carrier ? esc(st.carrier) : '';
      var stNumber = st.number ? esc(st.number) : '';
      var stUrl = st.url ? esc(st.url) : '';
      var reqRow = '';
      if (b.sampleRequest && (b.sampleRequest.sentAt || b.sampleRequest.scheduledFor)) {
        var when = b.sampleRequest.sentAt ? (formatDate(b.sampleRequest.sentAt) || b.sampleRequest.sentAt)
          : 'scheduled for ' + (formatDate(b.sampleRequest.scheduledFor) || b.sampleRequest.scheduledFor);
        reqRow = '<div class="rqd-sample-tracking__k">Requested on</div>'
          + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static"><span class="rqd-sample-tracking__text">' + esc(when) + '</span>'
          + (b.sampleRequest.by ? '<span class="rqd-sample-tracking__by"> · ' + esc(b.sampleRequest.by) + '</span>' : '') + '</div>';
      }
      var delRow = '';
      if (trk) {
        var dTxt = (trk.delivery && (trk.delivery.deliveredAt || trk.delivery.status === 'DL'))
          ? 'Delivered' + (trk.delivery.deliveredAt ? ' ' + (formatDate(trk.delivery.deliveredAt) || '') : '')
          : (trk.trouble ? 'Carrier problem' + (trk.trouble.statusText ? ' · ' + String(trk.trouble.statusText) : '') : 'In transit');
        delRow = '<div class="rqd-sample-tracking__k">Status</div>'
          + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static"><span class="rqd-sample-tracking__text">' + esc(dTxt) + '</span></div>';
      }
      var stMeta = (st.savedBy && st.savedAt)
        ? '<div class="rqd-sample-tracking__meta">Saved by ' + esc(st.savedBy) + ' · ' + esc(formatDate(st.savedAt) || st.savedAt) + '</div>' : '';
      html += '<div class="rqd-detail__section-title rqd-detail__section-title--sub rqd-sample-second__sub" style="margin-top:14px;font-size:12px;">Shipment Tracking</div>';
      html += '<div class="rqd-sample-band rqd-sample-band--tracking rqd-sample-band--second-tracking">'
        + '<div class="rqd-sample-tracking rqd-sample-tracking--summary' + (trk ? ' rqd-sample-tracking--sent' : '') + '" data-ref="' + _qsRefEsc + '" data-lane="2" data-owner="' + stOwner + '">'
        + '<div class="rqd-sample-tracking__view">' + reqRow
        +   _viewRow('Carrier', 'carrier', stCarrier, 'Add carrier', false)
        +   _viewRow('Tracking #', 'number', stNumber, 'Add tracking number', false)
        +   _viewRow('Tracking URL', 'url', stUrl, 'Add tracking URL', true)
        +   delRow
        + '</div>'
        + '<div class="rqd-sample-tracking__row">'
        +   '<div class="rqd-carrier-ac rqd-sample-tracking__carrier-wrap">'
        +     '<input type="text" class="rqd-vendor-tracking__carrier rqd-sample-tracking__carrier" placeholder="Carrier..." autocomplete="off" maxlength="60" value="' + stCarrier + '">'
        +     '<div class="rqd-carrier-ac__menu" role="listbox"></div>'
        +   '</div>'
        +   '<input type="text" class="rqd-sample-tracking__number" placeholder="Tracking #" value="' + stNumber + '">'
        +   '<input type="text" class="rqd-sample-tracking__url" placeholder="Tracking URL" value="' + stUrl + '">'
        +   '<button type="button" class="rqd-sample-tracking__save rqd-invoice__link" data-ref="' + _qsRefEsc + '" data-lane="2" title="Record the carrier and tracking number for the second recipient\'s box. No email is sent; the buyer\'s notice picks it up.">Save</button>'
        +   '<button type="button" class="rqd-sample-tracking__cancel rqd-invoice__link" title="Discard these edits and go back to the summary.">Cancel</button>'
        + '</div>'
        + stMeta
        + '</div>'
        + '</div>';
    }
    html += '</div>';
    return slotOpen + html + '</div>';
  }
  window.RQD.sampleSecondaryBandHtml = sampleSecondaryBandHtml;

  /** Re-render the block in place from the row in memory (after a save, a request, a remove). Finds
      the block's `__slot` on whichever card drew it and swaps it, then shows or hides the
      "+2nd Recipient" button in the Job Information band (it is hidden while a block exists). */
  function rerenderSampleSecondary(ref) {
    var q = (window.RQD && typeof RQD.quoteByRef === 'function') ? RQD.quoteByRef(ref) : null;
    if (!q) return;
    var sel = (window.CSS && CSS.escape ? CSS.escape(ref) : ref);
    var qq = q.reference === ref ? q : Object.assign({}, q, { reference: ref });
    document.querySelectorAll('.rqd-sample-second__slot[data-ref="' + sel + '"]').forEach(function (el) {
      var sub = el.getAttribute('data-sub') === '1';
      var tmp = document.createElement('div');
      tmp.innerHTML = sampleSecondaryBandHtml(qq, { sub: sub });
      var fresh = tmp.firstElementChild;
      if (fresh) el.replaceWith(fresh); else el.remove();
    });
    var hasBlock = !!sampleSecondaryOf(qq);
    document.querySelectorAll('.rqd-sample-second__add-btn[data-ref="' + sel + '"]').forEach(function (btn) {
      btn.classList.toggle('rqd-sample-second__add-btn--off', hasBlock);
    });
  }
  window.RQD.rerenderSampleSecondary = rerenderSampleSecondary;
  /* END CUSTOM_SAMPLES2ND_BY_CLAUDE */

  /* The row's resolved project block. ⚠️ A STALE-BAKE BRIDGE LIVES HERE, AND ONLY HERE. The board
     is served from baked per-deal summaries, and a summary baked before 2026-09-06 has no
     `sampleProject` KEY at all — as opposed to `null`, which the worker writes when it looked and
     found nothing. Until the nightly bake (or /admin/dd-summary-bake) rewrites every row, a website
     sample lead would otherwise gate Request on a fact it plainly has in `leadResponses`. So an
     ABSENT key falls back to the form answers by the same key match the worker uses; a present key,
     null included, is trusted. The bridge costs nothing once the bakes catch up and can be removed
     the day every summary carries the key. */
  function sampleProjectFor(q) {
    if (!q) return null;
    if (Object.prototype.hasOwnProperty.call(q, 'sampleProject')) return q.sampleProject || null;
    var r = q.leadResponses;
    if (!r || typeof r !== 'object') return null;
    var type = '', sqft = '';
    Object.keys(r).forEach(function (k) {
      var norm = String(k).toLowerCase().replace(/[^a-z]/g, '');
      var v = String(r[k] == null ? '' : r[k]).trim();
      if (!v) return;
      if (!type && (norm.indexOf('projecttype') !== -1 || norm.indexOf('typeofproject') !== -1)) type = v;
      if (!sqft && (norm.indexOf('squarefeet') !== -1 || norm.indexOf('squarefootage') !== -1 || norm.indexOf('sqft') !== -1)) {
        var d = v.replace(/,/g, '').match(/\d[\d.]*/);
        if (d) sqft = d[0].replace(/\.0+$/, '');
      }
    });
    return (type || sqft) ? { type: type, squareFeet: sqft, source: 'form' } : null;
  }
  window.RQD.sampleProjectFor = sampleProjectFor;

  /** The gate's input, read off a dashboard row. */
  function sampleRequestGateInputFor(q) {
    var list = samplesListFor(q);
    var sp = sampleProjectFor(q) || {};
    return { samplesCount: (Array.isArray(list) ? list : []).length, type: sp.type || '', squareFeet: sp.squareFeet || '',
      hasAddress: sampleShipToFor(q).source !== 'none' };
  }
  window.RQD.sampleRequestGateInputFor = sampleRequestGateInputFor;

  /* ⚠️ There is no hint line under the buttons (Ryan 2026-09-06: *"Remove the text 'Request
     needs a sample list (use Adjust).'"*). The disabled Request button's `title` names the gaps;
     `refreshSampleRequestGate` keeps that title current. */

  /* Whether the signed-in rep may write the Project row: the deal's owner (ref prefix — the
     portal's ownership rule) or an admin. ⚠️ Adjust and Request stay admin-only; this row emails
     nobody, and it is the rep writing down what the customer said so admin CAN request. */
  function canEditSampleProject(q) {
    if (getIsAdmin()) return true;
    try {
      var sess = window.RepPortal && window.RepPortal.Auth && window.RepPortal.Auth.getSession();
      var code = sess && (sess.repCode || sess.code);
      var m = /^([A-Z]{2,4})-/.exec(String((q && q.reference) || ''));
      return !!(code && m && m[1] === String(code).toUpperCase());
    } catch (_) { return false; }
  }
  window.RQD.canEditSampleProject = canEditSampleProject;

  /** JOB INFORMATION — its own sub-section on the card (Ryan 2026-09-06: *"a new category on the
      card for 'JOB INFORMATION' which includes similar formatting to the SHIPMENT TRACKING category
      but holds the project type drop-down and the sq ft fill-in area"*). The heading is the same
      element Shipment Tracking uses and the body is the same two-column grid
      (`.rqd-sample-tracking__view`, label cells `.rqd-sample-tracking__k`), so the two sections
      read as one control in two places. Two rows: Project type, Square feet.
      `opts.readOnly` forces the text form; `opts.sub` marks the heading `--sub` for a card whose
      collapse rule keys on it (the quote-side card; see the note beside its Shipment Tracking). */
  /* CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE (Ryan 2026-10-03, JW-1300-A) — FEDEX PRIORITY OVERNIGHT ON OUR ACCOUNT.
     One box per recipient: the buyer's sits in Job Information (both cards draw that band through
     here), the second recipient's in its own band. Ticked = the ECORE work order opens with "Please ship
     these via FedEx Priority Overnight on our FedEx account #205130645 (not FedEx Ground)." and the
     subject ends "(Priority Overnight)". Saved at once to POST /sample-ship-speed; Request waits for the
     save (RQD.awaitSampleSaves). Absent on the row = FedEx Ground on ECORE's account. */
  function sampleOvernightFor(q, lane) {
    if (!q) return null;
    if (lane === 2) { var b = sampleSecondaryOf(q); return (b && b.priorityOvernight && b.priorityOvernight.on) ? b.priorityOvernight : null; }
    var f = q.samplePriorityOvernight;
    return (f && f.on) ? f : null;
  }
  window.RQD.sampleOvernightFor = sampleOvernightFor;
  /** Two cells for a `.rqd-sample-tracking__view` grid: the label and the box (or the words, read-only). */
  function sampleOvernightCellsHtml(q, lane, editable) {
    var on = !!sampleOvernightFor(q, lane);
    var ref = esc((q && q.reference) || '');
    /* Ryan 2026-10-03: no pill — the ticked box is the signal. */
    var v = editable
      ? '<label class="rqd-sample-overnight__label" title="Ships on OUR FedEx account, which costs us. Unticked, ECORE ships FedEx Ground on their account for free.">'
        + '<input type="checkbox" class="rqd-sample-overnight__box" data-overnight-lane="' + (lane === 2 ? '2' : '1') + '" data-ref="' + ref + '"' + (on ? ' checked' : '') + '>'
        + ' FedEx Priority Overnight (our account)</label>'
        + '<span class="rqd-sample-overnight__status" aria-live="polite"></span>'
      : '<span class="rqd-sample-tracking__text">' + (on ? 'FedEx Priority Overnight (our account)' : 'FedEx Ground (ECORE\'s account)') + '</span>';
    return '<div class="rqd-sample-tracking__k rqd-sample-overnight__k">Shipping</div>'
      + '<div class="rqd-sample-project__v rqd-sample-overnight" data-lane="' + (lane === 2 ? '2' : '1') + '">' + v + '</div>';
  }
  window.RQD.sampleOvernightCellsHtml = sampleOvernightCellsHtml;
  /* END CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE */

  function sampleProjectRowHtml(q, opts) {
    var o = opts || {};
    var ref = (q && q.reference) || '';
    var sp = sampleProjectFor(q) || {};
    var type = String(sp.type || '');
    var sqft = String(sp.squareFeet || '');
    var editable = !o.readOnly && canEditSampleProject(q);
    /* Ryan 2026-09-06: no "set by RW on 9/6/2026" record on the card — *"If it gets set, that's all
       we need."* The status span stays for the transient Saving… / Not saved messages only. */

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ REQUEST LIVES HERE, NOT BESIDE ADJUST.
       Ryan: *"for it to become ungated, you need to click 'Adjust' and also fill out the 'Project
       type' and 'sq ft' fields first... So it needs to be down a bit lower so it's more intuitive
       that both of those things are requirements."* The section is a band like the two above it:
       the grid on the left, the caller's Request button (`opts.requestBtnHtml`) on the right, 8px
       under the underline. Adjust stays on the Samples band, which is the first requirement; this
       band holds the other two and the button they unlock. */
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) - "+2nd Recipient" sits directly UNDER Request in
       this column (`--stack` makes it a column; see `sampleSecondaryAddBtnHtml`). */
    var _secondBtn = sampleSecondaryAddBtnHtml(q);
    var _actions = (o.requestBtnHtml || _secondBtn)
      ? '<div class="rqd-sample-request__actions rqd-sample-request__actions--stack">' + (o.requestBtnHtml || '') + _secondBtn + '</div>'
      : '';
    var html = '<div class="rqd-detail__section-title' + (o.sub ? ' rqd-detail__section-title--sub' : '')
      + ' rqd-sample-project__title" style="margin-top:18px;font-size:13px;">Job Information</div>';
    html += '<div class="rqd-sample-band rqd-sample-band--job">';
    html += '<div class="rqd-sample-project rqd-sample-tracking__view" data-ref="' + esc(ref) + '"' + (editable ? ' data-editable="1"' : '') + '>';
    if (!editable) {
      var EMPTY = '<span class="rqd-sample-tracking__empty">Not recorded yet</span>';
      html += '<div class="rqd-sample-tracking__k">Project type</div>'
        + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static">' + (type ? '<span class="rqd-sample-tracking__text">' + esc(type) + '</span>' : EMPTY) + '</div>'
        + '<div class="rqd-sample-tracking__k">Square feet</div>'
        + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static">' + (sqft ? '<span class="rqd-sample-tracking__text">' + esc(sqft) + ' sq ft</span>' : EMPTY)
        + '</div>'
        + sampleOvernightCellsHtml(q, 1, false); // CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE
      return html + '</div>' + _actions + '</div>';
    }
    /* A stored value that is not on the list (a legacy free-text answer, or "Other — …") is
       offered as its own option so the select never silently shows "Select one" over a real
       value — and never overwrites it on a blur that changed nothing. */
    var isOther = /^Other(\s|$)/.test(type);
    var listed = SAMPLE_PROJECT_TYPES.indexOf(type) !== -1;
    var selectVal = isOther ? 'Other' : type;
    var otherText = isOther ? type.replace(/^Other\s*(—|-)?\s*/, '') : '';
    html += '<div class="rqd-sample-tracking__k">Project type</div>'
      + '<div class="rqd-sample-project__v">'
      + '<select class="rqd-sample-project__select" data-sp-field="type" aria-label="Project type" title="Project type">'
      + '<option value="">Select one</option>'
      + SAMPLE_PROJECT_TYPES.map(function (t) {
          return '<option value="' + esc(t) + '"' + (t === selectVal ? ' selected' : '') + '>' + esc(t) + '</option>';
        }).join('')
      + (type && !listed && !isOther ? '<option value="' + esc(type) + '" selected>' + esc(type) + '</option>' : '')
      + '</select>'
      + '<input type="text" class="rqd-sample-project__other" data-sp-field="other" maxlength="100" placeholder="Describe the project"'
      +   ' aria-label="Project type (other)" value="' + esc(otherText) + '"' + (isOther ? '' : ' hidden') + '>'
      + '</div>'
      + '<div class="rqd-sample-tracking__k">Square feet</div>'
      + '<div class="rqd-sample-project__v">'
      + '<input type="number" class="rqd-sample-project__sqft" data-sp-field="squareFeet" min="1" step="1" inputmode="numeric"'
      +   ' placeholder="e.g. 2400" aria-label="Square feet" title="Square feet of the area" value="' + esc(sqft) + '">'
      + '<span class="rqd-sample-project__unit">sq ft</span>'
      + '<span class="rqd-sample-project__status" aria-live="polite"></span>'
      + '</div>'
      + sampleOvernightCellsHtml(q, 1, true); // CUSTOM_SAMPLEOVERNIGHT_BY_CLAUDE
    return html + '</div>' + _actions + '</div>';
  }
  window.RQD.sampleProjectRowHtml = sampleProjectRowHtml;
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  function buildLeadDetail(q) {
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — ⛔ EVERY SAMPLE CONTROL ON THIS CARD IS KEYED ON
       THE RECORD THAT OWNS THE LIST. A joined online sample request folded into an earlier deal
       (worker: lib/sample-fold.js, `samplesOriginRef` painted by applyFoldedSamplesReadThrough) is
       a lead row whose list, request, box and ladder all live on the origin — so Adjust, Request,
       the tracking form, Notify and Stop/Resume act on THAT ref, exactly as the quote-side card
       has done for an inheriting revision since 09-06. Nothing writes to the folded lead itself.
       ⚠️ Only sample controls use this; the lead-source select and the notes keep the deal's own ref. */
    var _qsRef = q.samplesOriginRef || q.reference;
    var _qsRefEsc = esc(_qsRef);
    var html = '';

    // CUSTOM_RFQPORTAL_BY_CLAUDE — lead detail panel (Ryan 2026-05-05 polish):
    // simplified — source heading with embedded link (no separate
    // "Submitted from" line, no link for phone leads), customer message
    // in a plain pending-style text box (read-only), no routing trail,
    // no prominent CTA (rep uses cog menu → Edit Quote instead).
    var srcGroup = q.leadSourceGroup || '';
    var srcIcon = srcGroup === 'web-form' ? '📝'
      : srcGroup === 'sample-request' ? '🧪'
      : srcGroup === 'rfq' ? '🛒'
      : srcGroup === 'manual' ? '📞'
      : srcGroup === 'email-lead' ? '📧'
      : srcGroup === 'chatbot' ? '💬'
      : '•';
    var srcLabel = srcGroup === 'web-form' ? 'Web Form'
      : srcGroup === 'sample-request' ? 'Sample Request'
      : srcGroup === 'rfq' ? 'Add to Quote'
      : srcGroup === 'manual' ? 'Phone'
      : srcGroup === 'email-lead' ? 'Email Lead'
      : srcGroup === 'chatbot' ? 'Chatbot'
      : (srcGroup || 'Unknown');
    // Phone + email-lead heading: drop the formName entirely. The
    // canonical formNames for these manual entries ("Phone Inquiry
    // (manual entry)" / "Email Inquiry (AI-extracted)") just repeat
    // what "📞 Phone" or "📧 Email Lead" already says. Strip the
    // dash-suffix and don't link the prefix either — neither flow
    // has a meaningful sourceUrl worth opening.
    var isPhone = srcGroup === 'manual';
    var isEmailLead = srcGroup === 'email-lead';
    var hideFormName = isPhone || isEmailLead;
    var formNameStr = hideFormName ? '' : (q.leadFormName || '');

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Sample-request
       channel folds into the source label: "Sample Request by phone"
       / "Sample Request by email" / "Sample Request via referral"
       instead of a separate channel pill next to the Initial
       Conversation Notes heading. Also strips the redundant
       "Samples request " prefix + parens from leadFormName so a
       migrated sample reads as "Sample Request by phone — migrated
       from pending RW-1111-A" instead of "Sample Request — Samples
       request (migrated from pending RW-1111-A)". */
    var srcLabelDisplay = srcLabel;
    if (srcGroup === 'sample-request' && q.sampleInquiryChannel) {
      var _ch = String(q.sampleInquiryChannel).toLowerCase();
      var _suffix = _ch === 'email' ? ' by email'
        : _ch === 'referral' ? ' via referral'
        : ' by phone';
      srcLabelDisplay = srcLabel + _suffix;
    }
    if (srcGroup === 'sample-request' && formNameStr) {
      // Strip leading "Samples request " or "Sample request " noise.
      formNameStr = formNameStr.replace(/^Samples? request\s+/i, '');
      // Strip surrounding parens if the result is now wrapped.
      formNameStr = formNameStr.replace(/^\((.+)\)$/, '$1');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-16) — Some legacy
         sample form names embed an explicit dash before the SHOP-ref
         ("Samples request - SHOP-1698"). After the prefix-strip
         above, that leading dash stays — and the heading join later
         prepends " — ", which renders as a visible double-dash
         ("Sample Request — - SHOP-1698"). Strip any leading hyphen /
         en-dash / em-dash + whitespace so the heading reads with a
         single separator. */
      formNameStr = formNameStr.replace(/^[—–\-]+\s*/, '');
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Heading: prefix is plain text, formName is the hyperlink (when sourceUrl
    // exists and the source has a meaningful URL). Link uses color:inherit so
    // it picks up the NEW LEAD theme color from the parent card.
    var prefixText = srcIcon + ' ' + esc(srcLabelDisplay);
    var formNameHtml = '';
    if (formNameStr) {
      var formNameSafe = esc(formNameStr);
      formNameHtml = ' &mdash; ' + ((q.leadSourceUrl && !hideFormName)
        ? '<a href="' + esc(q.leadSourceUrl) + '" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline;">' + formNameSafe + '</a>'
        : formNameSafe);
    } else if (q.leadSourceUrl && !hideFormName) {
      // No formName but we still have a sourceUrl — make the prefix the link
      prefixText = '<a href="' + esc(q.leadSourceUrl) + '" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline;">' + prefixText + '</a>';
    }
    var headingHtml = prefixText + formNameHtml;
    var sectionTitleLabel = 'Form Submission';
    /* Phase 1.37 — CFA-handoff leads come in with source='manual'
       (their "form" was a CFA conversation, not a storefront form),
       so the "Form Submission" card heading + "📞 Phone Inquiry"
       prefix were misleading. When q.cfaCode + q.handoffAt are
       present, replace the card title with "CFA Handoff" and the
       prefix with the CFA's name + handoff date so the rep sees
       who handed the lead off and when, at a glance. */
    if (q.cfaCode && q.handoffAt) {
      sectionTitleLabel = 'CFA Handoff';
      var _cfaDisplayName = q.handoffFromCfaName || q.cfaName || q.cfaCode;
      var _handoffDateStr = typeof formatDate === 'function' ? formatDate(q.handoffAt) : esc(q.handoffAt);
      headingHtml = '🎯 Handed off by ' + esc(_cfaDisplayName) + ' on ' + esc(_handoffDateStr);
    }

    html += '<div class="rqd-detail__card rqd-detail__lead" style="grid-column: span 2;">';
    // CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Section titles
    // inside this card all standardize to 13px (was 12px default for
    // Form Submission, 13px inline for the rest). Color comes from
    // the status-themed .rqd-detail__section-title rules (yellow on
    // awaiting/lead, etc.) — keeps every heading consistent.
    html += '<div class="rqd-detail__section-title" style="font-size:13px;">' + sectionTitleLabel + '</div>';
    html += '<div style="font-size:14px;font-weight:600;color:var(--rq-text);margin-bottom:14px;">' + headingHtml + '</div>';

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ THE SAMPLES BLOCK MOVED TO THE TOP.
       Ryan: *"this entire header should be moved under the FORM SUBMISSION one so it's more close to
       the top."* It used to render below Shipment Tracking and the on-hold banner, so WHAT THE
       CUSTOMER ASKED FOR sat underneath the logistics of sending it — and on a long lead card it was
       below the fold. The list is the reason the deal exists; the tracking number is bookkeeping about
       it. ⚠️ Deliberately ABOVE the dead-lead and on-hold banners too: those say what state the deal
       is in, and this says what it is about, which is the thing you need first either way. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Samples block for
       sample-request leads. Renders before the Customer Message box
       using bullet-dash list format ("- ECORE Beast — Gray"). Matches
       the rep notification email exactly. For webhook-sourced sample
       leads, leadMessage is empty so the message box below skips
       naturally; for + New Sample manual entries where the rep typed
       call notes, BOTH the samples block + the Initial Conversation
       Notes box render. */
    var _leadSamples = Array.isArray(q.leadSamples) ? q.leadSamples.filter(function(s){return s && String(s).trim();}) : [];
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — ⛔ THE BLOCK ALSO RENDERS WHEN THE LIST IS
       EMPTY, for an admin on a sample deal. It used to be gated purely on `length > 0`, which
       meant a deal with no samples had no SAMPLES heading — and therefore nowhere to put the
       Adjust button, so the one deal that most needs a list was the one that could not be given
       one. The vendor email already anticipates this case in words: *"(no samples are recorded on
       this deal — add them before sending)"*. It could say that and offer no way to act on it.
       ⚠️ Scoped to sample-request deals AND to admins, so no other lead type grows an empty
       heading it has no use for. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) - ⛔ SCOPED TO SAMPLE-REQUEST DEALS, AND THE
       LENGTH TEST WENT WITH IT. The quote-level card (dashboard-sample-commission.js) now renders
       this same block for deals this one does NOT claim, and the two must never both draw it: they
       share the class `.rqd-lead-samples[data-ref]`, which is the single selector the picker
       repaints by, so a duplicate means a save that updates whichever card happens to be first in
       the DOM and silently not the other.
       ⛔ THE OLD `_leadSamples.length > 0` MADE OWNERSHIP DEPEND ON THE DATA, WHICH IS UNSTABLE.
       On an rfq-sourced deal the quote card owns the block, writes to `lead.samples` (the resolver
       is lead-first), and on the next reload a length-based gate here would have switched
       ownership - the list Ryan had just built would appear to move to a different card. Ownership
       is decided by what the deal IS, which does not change when somebody saves.
       ⚠️ `samples` is written at intake only by the sample-request webhook ("Empty for non-sample
       leads", lead-routes.js), so no existing deal loses a block it was showing. */
    if (leadSamplesBlockRenders(q)) {
      var _samplesLabel = _leadSamples.length === 1 ? 'Sample' : 'Samples';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — "Request Samples": admin asks ECORE to
         ship the box. Ryan's flow is that the rep calls first to confirm the list and the
         address, so this button is the step AFTER that conversation, not a substitute for it.

         ⛔ ADMIN ONLY, and it lives on the Samples block rather than beside Shipment Tracking
         because it is what happens BEFORE a tracking number exists. Tracking is the answer
         that comes back from this.

         ⚠️ Reads "Requested" once a request has gone out, but stays clickable — asking twice
         is a legitimate thing to do (they lost the first one, the list changed) and the
         composer says so before it sends. */
      /* \u26a0\ufe0f THROUGH THE SHARED RESOLVER, like the quote-side copy in dashboard-sample-commission.js
         already does. Reading `q.sampleRequest` directly is right only while the lead owns the
         list, which is the condition this branch happens to be under \u2014 but the two surfaces
         answering one question two ways is how they drift the day either gate moves. */
      var _srReq = (typeof sampleRequestFor === 'function') ? sampleRequestFor(q)
        : (q.sampleRequest || null);
      var _srSent = !!(_srReq && _srReq.sentAt);
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-24) - SCHEDULED GHOSTS TOO. A request clicked outside the
         vendor send window (6am-8pm) is parked with `scheduledFor` and no `sentAt`; the button stayed in
         the full accent saying "Request" until the morning cron sent it. Ryan: *"should the 'Request'
         button be ghosted/inverted color-wise since we've now done that, as a visual reminder?"* - yes,
         as "⌛ Scheduled" in the same outline look (his call: *"I think we use that same visual in other
         places on the DD when we schedule stuff"*). `_srGone` drives the LOOK (ghost, Adjust's outline,
         the header tick); `_srSent` keeps driving what only a real send unlocks ("Request more"). */
      var _srSched = !!(_srReq && !_srReq.sentAt && _srReq.scheduledFor);
      var _srGone = _srSent || _srSched;
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — titles on the list that no ask has named yet.
         With a request gone and something new, the button reads "Request more" in the live look:
         Ryan: *"I'd like it if the 'Request' button changed to 'Request x2' or 'Request Again'."* */
      var _srNew = _srSent ? sampleNewCountFor(q) : 0;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) \u2014 \u26d4 GHOSTED ONCE IT HAS GONE, AND STILL
         CLICKABLE. Ryan: *"I want the 'Request' button to gray/ghost out so I know it was already
         clicked. Text can also change to 'Requested'."*

         The ask is a STATUS signal \u2014 "so I know it was already clicked" \u2014 and that is what the
         dimming provides. \u26d4 It is deliberately not `disabled`: asking twice is a legitimate thing
         to do (they lost the first one, or the list changed under Adjust), the composer already
         stops and confirms with *"A sample request has already gone out for X. Send another one?"*,
         and `/sample-request-compose` returns `alreadyRequested` for exactly that purpose. A hard
         disable would delete a capability and orphan that confirm.
         \ud83d\udccc The date it went out is on the Sample Tracking card now, so the button no longer has to
         carry the whole answer \u2014 which is why the \u2713 could go and leave a plain word. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) \u2014 \u26d4 GATED. No list, no project type or no
         square footage \u2192 `disabled` + `--blocked`, and the hint line under the buttons says which.
         The "sent" ghost is a different state and stays clickable; see the helpers above. */
      var _srGate = sampleRequestGate(sampleRequestGateInputFor(q));
      var _srBtn = getIsAdmin()
        ? '<button type="button" class="rqd-sample-request rqd-invoice__link'
            + (_srGone && !(_srNew > 0) ? ' rqd-sample-request--sent' : '')
            + (_srSched ? ' rqd-sample-request--scheduled' : '')
            + (_srNew > 0 ? ' rqd-sample-request--more' : '')
            + (_srGate.ready ? '' : ' rqd-sample-request--blocked') + '" data-ref="' + _qsRefEsc + '"'
            + (_srGate.ready ? '' : ' disabled')
            + ' title="' + (!_srGate.ready
                ? esc('Not ready to send: needs ' + _srGate.missing.join(', ') + '.')
                : _srNew > 0
                ? esc(_srNew + ' new sample' + (_srNew === 1 ? '' : 's') + ' on the list not yet requested. Opens a reply on the original request asking ECORE to ship them.')
                : _srSched
                ? esc('Scheduled for ' + (formatDate(_srReq.scheduledFor) || _srReq.scheduledFor) + ' (the next send window). Click again only to replace the queued email.')
                : _srSent
                ? esc('Already requested' + (_srReq && _srReq.sentAt ? ' on ' + formatDate(_srReq.sentAt) : '')
                  + '. Click again only if they lost it or the list has changed \u2014 the composer will ask you to confirm.')
                : 'Email ECORE\'s samples desk as Abby, asking them to ship these. Opens the composer so you can read it first.')
            + '">'
            /* Ryan 2026-09-06: "Sent", in the Edit button's outline look — see `--sent` in the CSS. */
            + (_srSched ? '⌛ Scheduled' : _srSent ? (_srNew > 0 ? 'Request more' : 'Sent') : 'Request') + '</button>'
        : '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) \u2014 ADJUST, to the LEFT of Request.
         Ryan: *"a button which allows deal owners (admin only right now) to adjust the samples
         we're requesting\u2026 I think it should be next to the request button, to the left of it."*

         \u26d4 FIRST IN THE SAME ROW, NOT A ROW OF ITS OWN. `.rqd-sample-request__actions` is already
         `flex-end` with an 8px gap and the 0.8 scale var, so putting this first in that div is the
         whole of "to the left of Request" \u2014 no new layout, no second inset to keep in step, and
         both buttons stay the same 112x28 box. See the header on that class for why Request left
         the heading row in the first place.

         \u26a0\ufe0f It reads "Adjust" rather than "Edit": the list already exists (the customer chose it),
         and this changes what WE ask the supplier for. */
      var _sadjBtn = getIsAdmin()
        /* Ryan 2026-09-06: once the request has gone, Adjust wears the same outline as Sent. */
        ? '<button type="button" class="rqd-sample-adjust rqd-invoice__link' + (_srGone ? ' rqd-sample-adjust--sent' : '') + '" data-ref="' + _qsRefEsc + '"'
            + ' title="Change which samples we ask ECORE to ship. Updates the list on this card, and the request email with it.">'
            /* Ryan 2026-09-06: "Add" until there is a list to adjust. The picker repaints the label
               after a save, so it flips the moment the first sample lands. */
            + (_leadSamples.length ? 'Adjust' : 'Add') + '</button>'
        : '';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — the button matches Notify Customer's box.
         Ryan: *"it should be right-aligned with the same size/placement as the Notify Customer button."*
         ⚠️ MATCHED BY SHARING THE EXPRESSION, NOT BY RETYPING THE NUMBER. Both buttons are
         `.rqd-invoice__link`, which sizes itself `calc(140px * var(--rqd-sold-input-scale, 1))`. Notify
         resolves to 112px because `.rqd-sample-tracking` declares that var as 0.8; declaring the same
         var lands this button on the same 112px without a second hard-coded width that would drift
         the day the modular grid is rescaled.

         ⛔ CORRECTION (Ryan 2026-08-25) — THE ABOVE NEVER HAPPENED ON SCREEN, FOR TWO REASONS, AND
         THE BUTTON HAS NOW LEFT THE HEADING ROW ENTIRELY.
         ─────────────────────────────────────────────────────────────────────────────────────────
         Ryan, looking at JW-1269-A: *"All 3 have a different UI... the Notify Customer button is
         right aligned while the other 2 are left aligned"* and *"request button seems taller than
         the Notify Customer button. Might be an optical illusion?"* Neither was an illusion.

         (1) ⛔ THE INLINE `justify-content:space-between` LOST. `.rqd-detail__section-title` declares
         `justify-content: flex-start !important`, and an `!important` declaration beats a plain
         inline style — so Request has sat immediately after the word SAMPLES since the day it
         shipped, never at the right edge the comment above claims.

         (2) ⛔ AND A 28px BUTTON DOES NOT FIT AN 18px HEADER. That header is `height: 30px`
         BORDER-BOX with `padding-bottom: 10px` and a `2px` underline, so its content box is 18px —
         a number three separate passes on 2026-08-22 converged on precisely so that every card's
         underline lands on the same y. A 28px `.rqd-invoice__link` centred in it overflows by 10px
         and bleeds across the rule, which is what read as "taller".

         📌 SO THE FIX IS NOT TO GROW THE HEADER. Growing it to fit moves every underline in the
         detail grid — the exact defect those three passes fixed. The button gets its OWN row
         instead (`.rqd-sample-request__actions`), which mirrors `.rqd-sample-tracking__actions`
         exactly: same flex-end, same 12px inset, same scale var. The two buttons are now the same
         box in the same kind of container, so they cannot drift apart again. */
      /* Ryan 2026-09-06: the ✓ once the request has gone to ECORE — the same span and modifier the
         other cards' done state uses, so "samples were ordered" reads at a glance. */
      html += '<div class="rqd-detail__section-title rqd-samples-title' + (_srGone ? ' rqd-detail__section-title--done' : '') + '" style="margin-top:18px;font-size:13px;">'
        + '<span>' + _samplesLabel + '</span>'
        + (_srGone ? '<span class="rqd-detail__section-done" title="' + (_srSched ? 'Sample request to ECORE is scheduled for the next send window' : 'Sample request sent to ECORE') + '">&#10003;</span>' : '')
        + '</div>'
        ;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — plain lines, no dashes, no box.
         Ryan: *"No need for the '-' at the beginning or the box outline around the samples."*
         The dashes were copied from the rep notification EMAIL, where a bullet earns its keep
         because plain-text mail has no other way to say "list". On a card that already has a
         SAMPLES heading over it, they are three characters of noise per row. */
      /* ⚠️ `data-ref` SO THE PICKER CAN REPAINT THIS IN PLACE. A save re-renders only this block
         from the response the worker just returned — reloading the whole card would collapse every
         other section the admin had open, and re-fetching would risk showing a different list from
         the one the save just wrote. ⛔ And the empty state says what it is: "none yet" is a deal
         waiting for a list, not a deal whose list failed to load. */
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — through the ONE renderer both cards and the
         picker's repaint use. A deal with one ask and one box renders exactly the joined lines it
         always has; a second ask, a second box or an unrequested addition renders a tag per sample. */
      var _samplesBody = sampleListHtml(q, _leadSamples);
      /* `data-count` is what the picker's gate refresh reads after a save — the number of lines it
         just painted, so the gate never has to parse the box's HTML for the "None yet" marker. */
      /* CUSTOM_SAMPLESROW_BY_CLAUDE (Ryan 2026-09-12) — ⛔ THE LIST AND ADJUST SHARE ONE ROW, AND
         ADJUST IS RIGHT-ALIGNED. Ryan: *"the 'Adjust' button should be right-aligned, instead of
         left. and the samples should be on the same row, not down a row from it."*

         ⛔ REUSES `.rqd-sample-band`, WHICH ALREADY DOES EXACTLY THIS. That class is the Shipment
         Tracking pattern — content `flex: 1 1 auto` on the left, the actions box `flex: 0 0 auto`
         on the right, both 8px under the underline — and its rules are ALREADY written for
         `.rqd-lead-samples` and `.rqd-sample-request__actions` as direct children (see
         dashboard.css). A hand-rolled row here would be a second inset and a second gap to keep in
         step with that one, which is the drift the band was created to end.

         ⚠️ DOM ORDER IS THE ALIGNMENT. The list must come FIRST: it is the flexible child, so it
         takes the slack and pushes the fixed-width actions box to the right edge. Emitting the
         button first (as this card did) is what put it on its own line to the left.

         ⚠️ This reverses the 2026-08-30 `--left` call for THIS card only. That modifier still
         exists and is still used by the Request button lower down; only the Samples heading's own
         Adjust moves. The quote-owned copy in dashboard-sample-commission.js was already
         right-aligned and is untouched.

         ⚠️ `.rqd-lead-samples` keeps its `data-ref` / `data-count` and is still a direct child of
         a container the picker can find — the save repaints THAT node's contents, so wrapping it
         is safe while moving or renaming it would not be. */
      html += '<div class="rqd-sample-band rqd-sample-band--samples">'
        + '<div class="rqd-lead-samples" data-ref="' + _qsRefEsc + '" data-count="' + _leadSamples.length + '">' + _samplesBody + '</div>'
        + (_sadjBtn ? '<div class="rqd-sample-request__actions">' + _sadjBtn + '</div>' : '')
        + '</div>';
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE — a folded request says where its samples live, like the quote-side
         card's inheriting note; every control above and below acts on that deal. */
      if (q.samplesOriginRef) {
        html += '<div class="rqd-sample-tracking__meta">Samples are recorded on <strong>' + esc(q.samplesOriginRef) + '</strong> · this request was folded into it, so Adjust, Request and the tracking below act there.</div>';
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-10) — PHASE 3: the boxes on this customer's OTHER
         grouped deals. Below the list and OUTSIDE `.rqd-lead-samples`, because the picker repaints
         that node from a save response that knows nothing about a sibling — leaving it inside would
         make the line vanish on the first Adjust and come back on the next board refetch. */
      html += sampleSiblingsHtml(q);
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — the gate's reason, then the Project row.
         The hint sits with the buttons it explains; the row sits under the list it completes. Both
         render for a rep too (read-only text) so the whole team sees the same facts. */
      /* Ryan 2026-09-06: no hint line — the disabled button's title carries the reason. Request
         sits on the Job Information band below, beside the two fields that gate it. The Shipping
         Address section sits between the two. */
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE — on a folded row the two rows save under the origin ref (the one
         field the helpers put on the element), the way the quote-side card already hands them a copy. */
      var _qsQ = q.samplesOriginRef ? Object.assign({}, q, { reference: _qsRef }) : q;
      html += sampleShipToRowHtml(_qsQ);
      html += sampleProjectRowHtml(_qsQ, { requestBtnHtml: _srBtn });
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: after the job, before the
         buyer's Shipment Tracking, keyed on the origin ref like everything above it. */
      html += sampleSecondaryBandHtml(_qsQ, {});
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    // Phase 1.37 — Surface the CFA's handoff notes inline if they
    // wrote any. Read-only paragraph; the rep also sees them in
    // the Activity transition entry.
    if (q.cfaCode && q.handoffAt && q.handoffNotes && String(q.handoffNotes).trim()) {
      // Phase 1.42 — Match the Source Context block pattern:
      // .rqd-detail__section-title heading (picks up themed yellow
      // underline on amber cards, blue on quoted, etc.) + body
      // wrapped in .rqd-lead-message-box (themed bg + border).
      html += '<div class="rqd-detail__section-title" style="margin-top:0;font-size:13px;">CFA Handoff Notes</div>';
      html += '<div class="rqd-lead-message-box" style="display:block;width:100%;padding:10px;border-radius:8px;font-size:13px;font-family:inherit;line-height:1.55;white-space:pre-wrap;word-break:break-word;margin-bottom:14px;">'
        + esc(q.handoffNotes).replace(/\n/g, '<br>')
        + '</div>';
    }
    // Phase 1.41 — BANT discovery snapshot the CFA captured before
    // handing off. Always visible on the CFA Handoff card so the
    // rep has full context without digging into emails / activity
    // entries. Skips empty fields. Authority Email + Phone render
    // as mailto:/tel: links for one-click outreach.
    if (q.cfaCode && q.handoffAt && q.discovery && typeof q.discovery === 'object') {
      var _qd = q.discovery;
      var _qdStr = function(v) { return v == null ? '' : String(v).trim(); };
      var _qBudget = _qdStr(_qd.budget);
      var _qAuthName = _qdStr(_qd.authorityName);
      var _qAuthTitle = _qdStr(_qd.authorityTitle);
      var _qAuthEmail = _qdStr(_qd.authorityEmail);
      var _qAuthPhone = _qdStr(_qd.authorityPhone);
      var _qNeed = _qdStr(_qd.need);
      var _qTimeline = _qdStr(_qd.timeline);
      var _bantRows = [];
      if (_qBudget) _bantRows.push(['Budget', esc(_qBudget)]);
      // Phase 1.42 — Authority consolidated to ONE row: name · title ·
      // email · phone (joined with " — "). Email + phone stay as
      // mailto:/tel: links so the rep can still one-click them.
      var _authParts = [];
      if (_qAuthName) _authParts.push(esc(_qAuthName));
      if (_qAuthTitle) _authParts.push(esc(_qAuthTitle));
      if (_qAuthEmail) _authParts.push('<a href="mailto:' + esc(_qAuthEmail) + '" style="color:inherit;text-decoration:underline;">' + esc(_qAuthEmail) + '</a>');
      if (_qAuthPhone) _authParts.push('<a href="tel:' + esc(_qAuthPhone) + '" style="color:inherit;text-decoration:underline;">' + esc(typeof fmtPhone === 'function' ? fmtPhone(_qAuthPhone) : _qAuthPhone) + '</a>');
      if (_authParts.length > 0) _bantRows.push(['Authority', _authParts.join(' — ')]);
      if (_qNeed) _bantRows.push(['Need', esc(_qNeed)]);
      if (_qTimeline) _bantRows.push(['Timeline', esc(_qTimeline)]);
      if (_bantRows.length > 0) {
        // Same Source-Context pattern: themed section title + body
        // wrapped in .rqd-lead-message-box so the bg + border pick
        // up the card's color scheme.
        html += '<div class="rqd-detail__section-title" style="margin-top:0;font-size:13px;">CFA Discovery (BANT)</div>';
        html += '<div class="rqd-lead-message-box" style="display:block;width:100%;padding:10px;border-radius:8px;font-size:13px;font-family:inherit;line-height:1.55;margin-bottom:14px;">'
          + '<div style="display:grid;grid-template-columns:auto 1fr;column-gap:12px;row-gap:4px;">'
          + _bantRows.map(function(row) {
              return '<div style="opacity:0.75;font-weight:600;white-space:nowrap;">' + esc(row[0]) + '</div>'
                + '<div style="word-break:break-word;">' + row[1] + '</div>';
            }).join('')
          + '</div>'
          + '</div>';
      }
    }

    // Dead-lead banner + Resurrect CTA. Surfaces when a rep expanded a
    // dead row and is reading the customer's old message — gives them a
    // one-click path to flip it back to NEW LEAD if they decide to chase.
    if (q.isDeadLead) {
      var deadAtStr = q.deadAt ? formatDate(q.deadAt) : '';
      var deadByStr = q.deadBy ? esc(q.deadBy) : '';
      var deadBanner = '💀 Marked dead' + (deadAtStr ? ' on ' + esc(deadAtStr) : '') + (deadByStr ? ' by ' + deadByStr : '') + '.';
      html += '<div style="background:#0a0a0a;color:#f5f5f5;border:1px solid #404040;border-radius:8px;padding:10px 12px;margin-bottom:14px;display:flex;align-items:center;justify-content:space-between;gap:10px;font-size:13px;">'
        +   '<span>' + deadBanner + '</span>'
        +   '<button type="button" class="rqd-resurrect-lead-btn" data-ref="' + esc(q.reference) + '" style="background:#fafafa;color:#0a0a0a;border:1px solid #fafafa;border-radius:6px;padding:6px 12px;font-weight:700;cursor:pointer;font-family:inherit;font-size:12px;">Resurrect</button>'
        + '</div>';
    }

    // Sample-row admin extras (no banner — the SAMPLE pill on the
    // parent row already conveys status; reps don't need a redundant
    // explanatory banner inside the detail panel).
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Persist the lead-
       level Shipment Tracking card through status changes. q.isSample
       flips off the moment a sample-source lead is held (status →
       'on_hold'), promoted, or otherwise transitioned, but
       lead.sampleTracking sticks around. Show the card whenever the
       tracking data exists, regardless of current status — admin still
       needs visibility/edit access on the shipment. */
    if (leadTrackingBlockRenders(q)) {
      // Admin-only sample tracking input. Persists carrier + tracking
      // number on the lead record; surfaces in the To Do column as the
      // shipped indicator once saved. Empty trackingNumber clears it.
      // (getIsAdmin() reads the closure-mutating var from main.js — see
      // module header re: init-deps-var-hoisting-trap.)
      if (getIsAdmin()) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ SHOW WHATEVER TRACKING THE DEAL
           ACTUALLY HAS, and remember which record it came from.

           `q.sampleTracking` is the LEAD's copy. A deal that came in as an `rfq` and had its
           sample shipment recorded on the quote has the number on `workstation_meta`
           (surfaced as `q.quoteSampleTracking`) and nothing on the lead — but this card still
           renders, because the gate above also fires on `isSample` / `sampleNotifiedAt`. So it
           drew three EMPTY boxes over a deal that has had a tracking number since July, and a
           save went to the lead endpoint, which refused it (RW-1241-A, 2026-08-20).

           ⚠️ `data-owner` is the single signal the Save and Notify handlers read to pick their
           endpoint. Deriving it from the CSS class — which is what they did — asks which CARD
           drew, not which RECORD owns the data, and those are not the same question. */
        var _stLead = (q.sampleTracking && q.sampleTracking.number) ? q.sampleTracking : null;
        var _stQuote = (q.quoteSampleTracking && q.quoteSampleTracking.number) ? q.quoteSampleTracking : null;
        var st = _stLead || _stQuote || {};
        var _stOwner = _stLead ? 'lead' : (_stQuote ? 'wm' : 'lead');
        var stCarrier = st.carrier ? esc(st.carrier) : '';
        var stNumber = st.number ? esc(st.number) : '';
        var stUrl = st.url ? esc(st.url) : '';
        var stMeta = (st.savedBy && st.savedAt)
          ? '<div class="rqd-sample-tracking__meta">Saved by ' + esc(st.savedBy) + ' · ' + esc(formatDate(st.savedAt) || st.savedAt) + '</div>'
          : '';
        // Shipment Tracking — formatted to match the Notes section
        // (rqd-detail__section-title heading with underline, inputs +
        // pill-shaped Save/Clear). The .rqd-sample-tracking wrapper
        // class stays as a JS hook for handleSampleTrackingSave.
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-08) — green checkmark on the
           Shipment Tracking header once the samples-shipped email has gone out
           (q.sampleNotifiedAt), matching the completed-card treatment used by
           sectionTitleHtml in dashboard-order-cards.js. */
        var _sampleDone = !!q.sampleNotifiedAt;
        html += '<div class="rqd-detail__section-title' + (_sampleDone ? ' rqd-detail__section-title--done' : '') + '" style="margin-top:18px;font-size:13px;">Shipment Tracking'
          + (_sampleDone ? '<span class="rqd-detail__section-done" title="Samples notified to customer">&#10003;</span>' : '')
          + '</div>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Restructured
           to single-row layout matching the FULFILLMENT card's bucket
           rows. All controls (carrier, tracking #, URL, Save) live in
           one .rqd-sample-tracking__row with the FULFILLMENT-card
           scaled widths so the visual rhythm matches across cards. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-23) — mark the row "sent" once the
           sample has gone out (customer notified OR a tracking # is on file) so the
           fields + Notify button ghost into the status-tinted completed look. */
        var _sampleSent = _sampleDone || !!(st && st.number);
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ ONCE THE CUSTOMER HAS BEEN TOLD,
           NOTIFY LEAVES THE MAIN DISPLAY. Ryan, on RW-1305-A: *"the customer was already notified
           about their samples, yet there's still a visible Notify Customer button. Redundant? …
           Just doesn't need to be on the main display that's all."*
           PM²⁶ made it recede to a quiet link, which was only half an answer — quieter is still
           there. It now moves INTO the edit row beside Save and Cancel and reads "Re-Notify".
           That is the right home: edit mode is already the manual-override surface a rep opens only
           when the automation got something wrong, which is exactly when re-sending is wanted.
           ⚠️ NOT DELETED. It stays the ONLY way this email is ever sent — `cron.js` never calls
           /notify-customer-samples — and it also stamps the 7-day auto-hold. Ryan: *"I see it almost
           never getting used"*; almost never is not never, and the cost of being wrong is a rep who
           cannot re-send a tracking email at all.
           📌 Before notifying it stays exactly where it was, full size, on the main display —
           that is the one moment it is the primary action on this card. */
        /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: while the second set has
           been requested and has no number, the buyer's notice is waiting for it, and the button says
           so. Still clickable — the handler confirms before sending the buyer's number alone.
           Ryan 2026-09-24 (later): "Awaiting Box 2" — 14 chars so it fits the same nowrap box as
           "Notify Customer" (the first wording, 18 chars, overflowed), and no fade: the `--waiting`
           class no longer changes the colour, so the button matches Request / Adjust / Notify. */
        var _pairWait = !_sampleDone && samplePairWaiting(q);
        var _notifyBtnHtml = '<button type="button" class="rqd-sample-notify rqd-invoice__link' + (_pairWait ? ' rqd-sample-notify--waiting' : '') + '" data-ref="' + _qsRefEsc + '"'
          + ' title="' + (_sampleDone
              ? 'Send the samples email to this customer again. They have already been notified once.'
              : _pairWait
              ? 'The second recipient\'s box has no tracking number yet. Abby\'s automatic notice waits for both; clicking here asks whether to send the buyer their own number now.'
              : 'Saves the tracking and opens the Notify Customer modal (Send Test or Send to Customer from there).')
          + '">' + (_sampleDone ? 'Re-Notify' : (_pairWait ? 'Awaiting Box 2' : 'Notify Customer')) + '</button>';
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ THIS CARD READS AS TEXT NOW, NOT AS A FORM.
           ─────────────────────────────────────────────────────────────────────────────────────────
           Ryan: *"Since AI will be filling this in automatically (FedEx Ground will be the default
           carrier, tracking number will be what samples@ emails us, and tracking URL should be an easy
           auto-fill once the other 2 go in), then we don't need big text input boxes… I still want to
           give humans the option to edit/input these if they want."*

           Three permanently-open text inputs is the shape of a form a human fills in every time. Once
           the machine fills them, the rep's job changes from ENTERING to READING — and three empty-
           looking boxes are a worse way to read three short facts than three lines of text. So the
           resting state is a label/value grid matching Form Responses further down the same card, and
           the inputs are still here, one click away, for the outlier nobody automated.

           ⛔ THE INPUTS ARE HIDDEN, NOT REMOVED — and they keep their classes and their position inside
           `.rqd-sample-tracking`. `handleSampleTrackingSave` finds carrier/number/url by querying the
           wrapper, so leaving the fields exactly where it expects them means the save path is untouched
           by a change that is entirely about how the card LOOKS.

           ⚠️ NOTIFY CUSTOMER MOVED OUT OF THE INPUT ROW. It used to sit inside `__row`, which now hides
           at rest — so leaving it there would have made the one control that emails a customer vanish
           until you clicked into edit mode. It gets its own always-visible actions line. It keeps its
           112px width for free: `.rqd-invoice__link` sizes off `--rqd-sold-input-scale`, which this
           wrapper declares as 0.8, so it resolves to the same box it had inside the row. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE SAME BAND AS THE QUOTE-SIDE CARD.
           Ryan: *"Do the same tracking band on the lead-side Form Submission card too."* The
           tracking block is the band's left half (summary grid capped in CSS, or the edit row once
           a cell is clicked); Notify Customer is the right half, 8px under the underline. Notify
           therefore moves OUT of the wrapper into the band — the notify handler finds the wrapper by
           `data-ref` when the button is not inside one, exactly as it already must on the quote-side
           card. After the customer has been told, Re-Notify stays inside the edit row (08-20). */
        /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — earlier boxes, read-only, ABOVE the live band:
           "sub headers for 'SHIPMENT 1' and 'SHIPMENT 2'". The single editable widget below stays the
           LATEST box (the save/edit/paint handlers find it by class inside one wrapper), and the two
           ladder rows after it stay adjacent, which the notice row's paint depends on. */
        html += priorShipmentsHtml(q);
        html += '<div class="rqd-sample-band rqd-sample-band--tracking">';
        html += '<div class="rqd-sample-tracking rqd-sample-tracking--summary' + (_sampleSent ? ' rqd-sample-tracking--sent' : '') + (_sampleDone ? ' rqd-sample-tracking--notified' : '') + '" data-ref="' + _qsRefEsc + '" data-owner="' + _stOwner + '">'
          + '<div class="rqd-sample-tracking__view">'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ WHEN WE ASKED, ABOVE WHAT CAME BACK.
             Ryan: *"I also think we need to record somewhere the date of the request… Perhaps down
             on the SMPLE TRACKING card lower down on the DD, we have a 'Requested on' date field
             above carrier/tracking #/tracking URL."*

             📌 NOTHING NEW IS STORED. `_recordRequest` has stamped `sampleRequest.sentAt` on the
             owning record since 2026-08-19 — it is what labels the Request button and what the open
             index is built from. The date existed and had nowhere to be read.

             ⛔ NOT A `_viewRow`. Every row that helper builds is click-to-edit and carries a
             `data-field` the save handler queries for; this is a RECORD of something that happened,
             not a field anybody may retype. Giving it the same affordance would offer an edit that
             silently does nothing, and `handleSampleTrackingSave` would start finding a fourth
             element it does not know about.

             ⚠️ SCHEDULED IS NOT SENT. A request held for business hours carries `scheduledFor` and
             no `sentAt`, so it says so rather than printing a date the supplier has not seen yet.
             The row is absent entirely when nothing has been asked — an empty "Requested on" over a
             deal nobody has requested reads as a missing step rather than an inapplicable one. */
          +   (function () {
                var r = (typeof sampleRequestFor === 'function') ? sampleRequestFor(q) : (q.sampleRequest || null);
                if (!r) return '';
                var when = r.sentAt
                  ? (formatDate(r.sentAt) || r.sentAt)
                  : (r.scheduledFor ? 'scheduled for ' + (formatDate(r.scheduledFor) || r.scheduledFor) : '');
                if (!when) return '';
                var rows = '<div class="rqd-sample-tracking__k">Requested on</div>'
                  + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static">'
                  +   '<span class="rqd-sample-tracking__text">' + esc(when) + '</span>'
                  +   (r.by ? '<span class="rqd-sample-tracking__by"> · ' + esc(r.by) + '</span>' : '')
                  + '</div>';
                /* CUSTOM_SAMPLES2ND_BY_CLAUDE — every further ask, in order, under the first. */
                (Array.isArray(r.additional) ? r.additional : []).forEach(function (a) {
                  if (!a) return;
                  var w = a.sentAt ? (formatDate(a.sentAt) || a.sentAt) : (a.scheduledFor ? 'scheduled for ' + (formatDate(a.scheduledFor) || a.scheduledFor) : '');
                  if (!w) return;
                  rows += '<div class="rqd-sample-tracking__k">More requested</div>'
                    + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static">'
                    +   '<span class="rqd-sample-tracking__text">' + esc(w) + '</span>'
                    +   (a.by ? '<span class="rqd-sample-tracking__by"> · ' + esc(a.by) + '</span>' : '')
                    + '</div>';
                });
                return rows;
              })()
          +   _viewRow('Carrier', 'carrier', stCarrier, 'Add carrier', false)
          +   _viewRow('Tracking #', 'number', stNumber, 'Add tracking number', false)
          +   _viewRow('Tracking URL', 'url', stUrl, 'Add tracking URL', true)
          + '</div>'
          + '<div class="rqd-sample-tracking__row">'
          +   '<div class="rqd-carrier-ac rqd-sample-tracking__carrier-wrap">'
          +     '<input type="text" class="rqd-vendor-tracking__carrier rqd-sample-tracking__carrier" placeholder="Carrier..." autocomplete="off" maxlength="60" value="' + stCarrier + '">'
          +     '<div class="rqd-carrier-ac__menu" role="listbox"></div>'
          +   '</div>'
          +   '<input type="text" class="rqd-sample-tracking__number" placeholder="Tracking #" value="' + stNumber + '">'
          +   '<input type="text" class="rqd-sample-tracking__url" placeholder="Tracking URL" value="' + stUrl + '">'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ SAVE, WITHOUT TELLING THE CUSTOMER.
             Notify Customer was the only control on this row, and it saves as a side effect — so
             correcting a tracking number meant emailing someone about it. Ryan, on a box that landed
             six weeks ago: *"technically in this case I don't want to notify them … just update the
             record."* The comment in the notify handler has claimed this button existed since May;
             its CSS was still here. The button was not. */
          /* Ryan 2026-09-06: Save is the shared status-coloured button, Cancel its outline twin — the
             same pair the quote-side card got the same day. */
          +   '<button type="button" class="rqd-sample-tracking__save rqd-invoice__link" data-ref="' + _qsRefEsc + '" title="Record the carrier and tracking number on this deal. No email is sent.">Save</button>'
          +   '<button type="button" class="rqd-sample-tracking__cancel rqd-invoice__link" title="Discard these edits and go back to the summary.">Cancel</button>'
          +   (_sampleDone ? _notifyBtnHtml : '')
          + '</div>'
          + stMeta
          + '</div>'
          /* Notify at the band's right edge (only before the customer has been told), then the band closes. */
          + (_sampleDone ? '' : '<div class="rqd-sample-tracking__actions rqd-sample-tracking__actions--top">' + _notifyBtnHtml + '</div>')
          + '</div>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19) — THE FOLLOW-UP LADDER'S OFF SWITCH.
           Once the box is delivered this deal starts mailing the customer on its own, at
           08:05 MT, four times over three weeks, with nobody watching. Ryan: *"an unattended
           customer email needs an off switch before the crons go on. Put it in the portal,
           not a curl."*

           ⛔ THE RESTING STATE IS THE WORKING STATE. The status line is filled in by a fetch
           that may be slow, may fail, or may never fire on a re-render path nobody hooked —
           so Stop is live from the first paint and needs no state to work. Stopping a deal
           with nothing queued is a legitimate, useful action: it refuses the arm that the
           NEXT delivery scan would otherwise make. */
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ABBY'S AUTOMATIC "ON THE WAY" NOTICE.
           Sits ABOVE the follow-up row because it happens first: this one goes out when the
           tracking number lands, the ladder starts only after the box is scanned delivered.

           ⛔ IT WEARS THE LADDER'S OWN CLASSES so it inherits that row's layout and colours
           and needs no CSS of its own — but `data-state="child"` keeps it out of _ladderScan,
           which hydrates `[data-state="pending"]`. One fetch fills both rows; a second element
           with its own pending state would mean two requests to answer one card.

           ⚠️ Rendered adjacently and painted via previousElementSibling, with a class guard, so
           if this markup is ever reordered the notice simply stops painting rather than painting
           the wrong row. */
        html += '<div class="rqd-sample-ladder rqd-sample-ladder--notice" data-ref="' + _qsRefEsc + '" data-state="child">'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — wears the section-title class so it is a
             heading with the card's underline, like Shipment Tracking above it; the quote-side card
             has carried it since 08-30. Bare (no `--sub`) because this card's Shipment Tracking
             heading is bare too. */
          + '<span class="rqd-sample-ladder__label rqd-detail__section-title">Customer notice</span>'
          + '<span class="rqd-sample-ladder__state">&hellip;</span>'
          /* `hidden` until the status says a notice is queued (Ryan 2026-09-06) — `_noticePaint` owns it. */
          + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-notice__stop" data-ref="' + _qsRefEsc + '" hidden title="Stop Abby from automatically emailing this customer that this box shipped. Re-saving this tracking number cannot switch it back on; a later box arms its own notice.">Stop</button>'
          + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-notice__resume" data-ref="' + _qsRefEsc + '" hidden title="Allow the automatic notice again. This does NOT send or queue anything by itself.">Resume</button>'
          + '</div>';
        html += '<div class="rqd-sample-ladder" data-ref="' + _qsRefEsc + '" data-state="pending">'
          + '<span class="rqd-sample-ladder__label rqd-detail__section-title">Follow-up emails</span>'
          + '<span class="rqd-sample-ladder__state">&hellip;</span>'
          + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-ladder__stop" data-ref="' + _qsRefEsc + '" title="Stop the automatic follow-up emails for this shipment. A later delivery scan of THIS box cannot switch them back on; a later box arms its own follow-ups.">Stop</button>'
          + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-ladder__resume" data-ref="' + _qsRefEsc + '" hidden title="Allow the automatic follow-ups again. This does NOT send or queue anything by itself.">Resume</button>'
          + '</div>';
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      }
    }

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ THE ON-HOLD BANNER WAS DELETED HERE.
       Ryan, on RW-1335-A after leaving a voicemail: *"I don't think this section is even
       necessary. It's now redundant, since notes about it being held now live in the DEAL FEED."*
       Verified before removing — every fact and the one control it carried exists elsewhere:
         · who / when / the reason → `/lead-hold` unshifts a structured entry onto
           `lead.noteEntries` carrying `text` (the reason, or a deterministic
           "Put on hold until <date>." when the rep left it blank), `by`, `ts`, `followUpAt`
           (the hold-until) and a `transition` pill. The feed says MORE than the banner did.
         · the hold-until date → also the ⏳ countdown pill in the To Do column.
         · Wake Now → the SAME handler is already on the row as "⏵ Remove Hold"
           (`.rqd-wake-lead-btn`, dashboard-quote-row.js), plus Edit Hold Until in the cog.
       ⚠️ So this was a third copy of one fact and a second copy of one button, sitting at the
       top of the card pushing the actual content down. Its `--banner` hover rule in
       dashboard.css went with it rather than being left to rot. */

    // Notes/message — plain text box (matches the pending notes style),
    // read-only. Heading shifts based on origin: customer-typed message
    // (storefront forms / chatbot / sample webhook) reads "Customer
    // Message"; rep-typed call notes (manual phone/email + New entries)
    // read "Initial Conversation Notes" with the channel pill so the
    // dashboard reflects how the lead came in.
    // Phase 1.39 — CFA-handoff leads relabel this section to "Source
    // Context" since q.leadMessage on those rows is typically the
    // Lead Generator / Deal Finder enrichment summary (website,
    // category tags), NOT a customer message or a call note. The
    // actual handoff context lives in the CFA Notes block above +
    // the Activity log's transition entry.
    if (q.leadMessage && String(q.leadMessage).trim()) {
      // q.leadCaptureSource is the lead's intake source ('manual',
      // 'email-lead', 'sample-request', 'contact-form', etc.).
      // q.leadSource is reserved for the COMMISSION grouping
      // ('outbound'/'inbound'/'shared') — different concept entirely.
      var _capture = q.leadCaptureSource || '';
      var _isCfaHandoff = !!(q.cfaCode && q.handoffAt);
      var _isManualEntry = !_isCfaHandoff && (
          _capture === 'manual'
        || _capture === 'email-lead'
        || (_capture === 'sample-request' && q.leadFormId && /^manual/i.test(String(q.leadFormId)))
      );
      var _label = _isCfaHandoff
        ? 'Source Context'
        : (_isManualEntry ? 'Initial Conversation Notes' : 'Customer Message');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-15) — Channel pill
         removed entirely. The same signal ("by phone" / "by email" /
         "via referral") now folds into the Form Submission heading
         at the top of the lead card — having it in both places was
         redundant and the heading is the more discoverable surface.
         Manual phone / email-lead never had a pill (their source
         heading already carries it); this just brings sample-request
         into the same pattern. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Section title uses
         .rqd-detail__section-title to match Form Submission /
         Shipment Tracking / Pages Viewed / Form Responses /
         Attachments. Was a custom <label> with its own font color,
         which made Customer Message read as a different visual
         tier from the other subsection headings. */
      html += '<div class="rqd-detail__section-title" style="margin-top:18px;font-size:13px;">' + _label + '</div>';
      /* Phase 1.40 — On CFA-handoff rows, auto-linkify URLs in the
         message body so the imported "Website: https://…" lines are
         click-to-open. Non-CFA messages keep their existing raw
         render (worker pre-sanitizes them with allowed inline tags). */
      var _msgBody;
      if (_isCfaHandoff) {
        var _msgEscaped = esc(q.leadMessage || '');
        // Inherit text color so the underline reads cleanly against
        // the card's themed background (brand-blue was getting lost
        // on amber NEW LEAD bg). Underline-only = "click me" signal.
        _msgBody = _msgEscaped.replace(
          /(https?:\/\/[^\s<>"]+)/gi,
          '<a href="$1" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline;">$1</a>'
        );
      } else {
        _msgBody = q.leadMessage || '';
      }
      html += '<div class="rqd-lead-message-box" style="display:block;width:100%;min-height:80px;max-height:280px;overflow-y:auto;padding:10px;border-radius:8px;font-size:13px;font-family:inherit;line-height:1.55;white-space:pre-wrap;word-break:break-word;">' + _msgBody + '</div>';
    }

    // Pages viewed — visitor's session history before submitting the form
    // (e.g. "1. Home (6 sec)\n2. ECORE Athletic (14 sec)\n…"). Heading
    // matches Form Responses (rqd-detail__section-title — yellow + bordered
    // when the parent card is in awaiting/lead theme). Body keeps the
    // scrollable text box because pages-viewed is multi-line, not pairs.
    // No min-height — short page-history lists were leaving an awkward
    // empty band of dead space before the next section.
    if (q.leadPagesViewed && String(q.leadPagesViewed).trim()) {
      html += '<div class="rqd-detail__section-title" style="margin-top:18px;font-size:13px;">Pages Viewed</div>';
      html += '<div class="rqd-lead-pages-box" style="display:block;width:100%;max-height:280px;overflow-y:auto;padding:8px 10px;border-radius:8px;font-size:13px;font-family:inherit;line-height:1.55;white-space:pre-wrap;word-break:break-word;">' + esc(q.leadPagesViewed || '') + '</div>';
    }

    /* ── THE WEIGHT DESIGNER'S SET, AS LINE ITEMS ────────────────────────────────────────────
       Ryan 2026-08-24: *"the 'quote' section on the DD is just one giant paragraph and is hard to
       follow especially if someone is requesting pricing on multiple 3D models/weights."*

       Two things made it a paragraph. The `Quote` response is `\n`-joined, and it renders into HTML
       where newlines collapse — so a three-product quote arrived as one run-on sentence. And even a
       single product is a long comma run of denominations.

       So the dashboard now draws what the rep EMAIL has drawn since 2026-08-15, off the same
       `designItems` field: one block per product, one quantity per line. ⛔ And the `Quote` row is
       suppressed when this renders, exactly as the email suppresses it — the whole point is one
       rendering of these numbers, not two. */
    var designItems = Array.isArray(q.leadDesignItems) ? q.leadDesignItems : [];
    if (designItems.length) {
      html += '<div class="rqd-detail__section-title" style="margin-top:18px;font-size:13px;">'
        + (designItems.length === 1 ? 'The Design' : 'The Design (' + designItems.length + ')') + '</div>';
      html += '<div style="display:flex;flex-direction:column;gap:10px;font-size:13px;">';
      designItems.forEach(function(it) {
        var sub = [it.kind, it.configuration].filter(function(x) { return String(x || '').trim(); }).join(' · ');
        /* The colour chip earns its place HERE, unlike on the buyer's copy: a school ordering white
           45s and green 25s sends the same product as two blocks, and without it a rep sees two
           headings reading word for word the same. */
        var chip = /^#[0-9a-fA-F]{3,8}$/.test(String(it.plateColor || ''))
          ? '<span style="display:inline-block;width:11px;height:11px;border-radius:3px;vertical-align:baseline;'
            + 'margin-right:6px;border:1px solid rgba(0,0,0,.3);background:' + esc(it.plateColor) + ';"></span>'
          : '';
        var lines = Array.isArray(it.lines) ? it.lines : [];
        /* ⚠ THE "NO QUANTITY" STATES ARE KEPT AND MADE LOUD. `quantityNote` distinguishes "wants to
           work it out with a rep" from "designed it and left the grid blank", and each is a
           different next action — this is a rep-facing surface, so the ⚠ stays. */
        var body = lines.length
          ? lines.map(function(l) { return esc(String(l && l.text ? l.text : '')); }).filter(Boolean).join('<br>')
          : '<span style="color:#eab308;">' + esc(String(it.quantityNote || 'No quantity given')) + '</span>';
        html += '<div>'
          + '<div style="color:var(--rq-text);font-weight:600;">' + chip + esc(String(it.label || ''))
          + (sub ? '<span style="color:var(--rq-text-secondary);font-weight:400;"> — ' + esc(sub) + '</span>' : '')
          + '</div>'
          + '<div style="color:var(--rq-text-secondary);line-height:1.6;margin-top:2px;">' + body + '</div>'
          + '</div>';
      });
      html += '</div>';
    }

    // Form responses — table of question → answer
    var responses = q.leadResponses || {};
    var responseKeys = Object.keys(responses).filter(function(k) {
      var v = responses[k];
      if (v === null || v === undefined || String(v).trim() === '') return false;
      // See the design block above — the same numbers, rendered better, directly on top of this.
      if (designItems.length && String(k).toLowerCase() === 'quote') return false;
      return true;
    });
    if (responseKeys.length > 0) {
      html += '<div class="rqd-detail__section-title" style="margin-top:18px;font-size:13px;">Form Responses</div>';
      // Div-grid instead of <table>: theme's main.css globally applies
      // `td { border: 1px solid; padding: 1em; }` which boxed every cell
      // and clashed with the NEW LEAD card theme. Divs sidestep that.
      html += '<div style="display:grid;grid-template-columns:auto 1fr;column-gap:12px;row-gap:4px;font-size:13px;">';
      responseKeys.forEach(function(k) {
        var raw = String(responses[k]);
        /* A response whose WHOLE value is one URL becomes a real link with a copy button
           (Ryan 2026-08-24). Until now every one of these rendered as escaped plain text, so a rep
           wanting to send a customer their design link had to select the string by hand out of a
           grid cell. Multi-line and mixed values stay as text — a partial linkifier on a value that
           is a sentence containing a URL is a different job and gets it wrong more often. */
        /* ⛔ LINE BREAKS THE VALUE ACTUALLY CONTAINS ARE PRESERVED. Several responses are `\n`-joined
           lists (`Buyer attachments`, and `Quote` on any lead older than the design block above) and
           this cell renders into HTML, where newlines collapse to a single space — which is how a
           multi-line answer became one run-on paragraph. Escaped FIRST, then the breaks are put back
           as markup, so nothing can be smuggled in through the value. */
        var cell = _URL_ONLY.test(raw.trim())
          ? _linkWithCopy(raw.trim(), raw.trim())
          : esc(raw).replace(/\r?\n/g, '<br>');
        html += '<div style="color:var(--rq-text-secondary);font-weight:600;white-space:nowrap;">' + esc(k) + '</div>' +
          '<div style="color:var(--rq-text);word-break:break-word;">' + cell + '</div>';
      });
      html += '</div>';
    }

    // Attachments
    var attachments = q.leadAttachments || [];
    if (Array.isArray(attachments) && attachments.length > 0) {
      html += '<div class="rqd-detail__section-title" style="margin-top:18px;font-size:13px;">Attachments</div>';
      html += '<ul style="margin:0;padding:0 0 0 20px;font-size:13px;">';
      attachments.forEach(function(a, i) {
        var url = typeof a === 'string' ? a : (a && a.url) || '';
        var name = typeof a === 'object' && a && a.name ? a.name : ('Attachment ' + (i + 1));
        if (!url) return;
        /* ⛔ RELATIVE URLs MUST BE RESOLVED AGAINST THE WORKER, NOT THIS PAGE. Weight Designer
           leads store artwork links as "/files/wd-artwork/…?sig=…" ON PURPOSE — the email
           templates prepend the worker origin themselves, so an absolute value there would
           produce a doubled host. This card never prepended anything, so the browser resolved
           them against blueskyfitsupply.com and every artwork link in the dashboard opened a
           Shopify 404 (Ryan 2026-08-24: "when I click either one it opens an error window").
           Absolute URLs pass through untouched, which is what the design links need. */
        html += '<li style="margin:3px 0;">' + _linkWithCopy(_resolveAttachmentUrl(url), name) + '</li>';
      });
      html += '</ul>';
    }

    // (No routing trail, no prominent Build Quote button — rep uses cog → Edit Quote)
    html += '</div>';
    return html;
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-24) — links you can OPEN or COPY, in one row.
   *
   * Two complaints, one shape. The Attachments links were dead (relative URLs resolved against the
   * storefront), and the Form Responses URLs were not links at all — escaped text a rep had to
   * select by hand to paste a design link into a customer email.
   *
   * ⛔ BOTH AFFORDANCES, NOT ONE. Click-to-copy alone would take away seeing the logo; a plain link
   * alone leaves the copying problem that started this. The name opens; the button beside it
   * copies. Neither gesture has to be discovered, because both are visible.
   */
  var WORKER_ORIGIN = 'https://rfq-worker.ryan-29b.workers.dev';
  var _URL_ONLY = /^https?:\/\/\S+$/;

  function _resolveAttachmentUrl(u) {
    var s = String(u || '');
    return s.charAt(0) === '/' ? WORKER_ORIGIN + s : s;
  }

  /* ⚠ THE LINK COLOUR IS `--rq-text`, matching the Form Responses values beside it (Ryan: the
     Attachments blue "should be the same font color used in the FORM RESPONSES section"). The
     underline stays — it is what still says "clickable" once the colour no longer does. */
  function _linkWithCopy(url, label) {
    var safeUrl = esc(url);
    return '<a href="' + safeUrl + '" target="_blank" rel="noopener" '
      + 'style="color:var(--rq-text);text-decoration:underline;text-underline-offset:2px;">'
      + esc(label) + '</a>'
      /* ⛔ NO INLINE STYLE ON THIS BUTTON. It carried its own `border:1px solid var(--rq-border)`,
         and an inline declaration beats every stylesheet rule no matter how specific — so the
         per-status tint groups in dashboard.css could never colour it and it would have stayed grey
         on every card. All of its styling lives in the sheet under `.rqd-copy-link`. */
      + '<button type="button" class="rqd-copy-link" data-copy="' + safeUrl + '" title="Copy link">copy</button>';
  }

  /* ONE document-level listener, installed once. The detail card is rebuilt by innerHTML on every
     render, so a handler bound to the buttons themselves would be thrown away with them — the same
     re-render trap that eats busy state elsewhere in this dashboard. Delegation survives it. */
  var _copyBound = false;
  function _bindCopyLinks() {
    if (_copyBound) return;
    _copyBound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('.rqd-copy-link');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var url = btn.getAttribute('data-copy') || '';
      if (!url) return;

      function flash(ok) {
        var prev = btn.textContent;
        btn.textContent = ok ? 'copied' : 'failed';
        setTimeout(function () { btn.textContent = prev; }, 1400);
      }
      /* execCommand fallback for a non-secure context — the portal is https, but a preview theme
         on a myshopify preview URL is not always, and a copy button that silently does nothing is
         worse than one that says "failed". */
      function fallback() {
        try {
          var ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          var ok = document.execCommand('copy');
          document.body.removeChild(ta);
          flash(ok);
        } catch (err) { flash(false); }
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () { flash(true); }, fallback);
      } else {
        fallback();
      }
    });
  }

  function initLeadDetail(deps) {
    deps = deps || {};
    esc = deps.esc;
    _bindCopyLinks();
    formatDate = deps.formatDate;
    fmtPhone = deps.fmtPhone;
    getIsAdmin = deps.getIsAdmin || function() { return false; };
  }

  /* Repaint the Shipment Tracking summary from a record, after a save lands.
     ⛔ EXPORTED RATHER THAN REIMPLEMENTED IN main.js. The save handler lives over there and needs
     to refresh these three cells, but "how a tracking value looks" — the ellipsis span, the ↗ on
     the URL only, the italic prompt when a field is empty — must have exactly ONE definition or
     the card drifts into two appearances of the same fact depending on whether you loaded the
     board or just saved. Same reason `tone` rides along with `sampleDeliveryState()`.
     ⚠️ Values are escaped HERE, because `_viewRow` takes them pre-escaped (the render path feeds
     it values that were escaped further up). */
  function paintSampleTrackingView(wrap, rec) {
    if (!wrap) return;
    var view = wrap.querySelector('.rqd-sample-tracking__view');
    if (!view) return;
    rec = rec || {};
    /* Through the shared builder, not three _viewRow calls of its own — otherwise the repaint and
       the first render are two places that could disagree about the same three rows. */
    view.innerHTML = buildSampleTrackingView(
      esc(rec.carrier || ''), esc(rec.number || ''), esc(rec.url || '')
    );
  }

  /* The three summary rows, for any card that wants them.
     ⛔ EXPORTED SO THE QUOTE-SIDE CARD SHARES THIS ONE DEFINITION rather than growing a parallel
     copy in dashboard-sample-commission.js. "How a tracking value looks" — the ellipsis span, the
     ↗ on the URL row only, the italic prompt when a field is empty — must be decided once, or the
     two cards drift into two appearances of the same three facts. Same reason
     paintSampleTrackingView is exported instead of reimplemented in main.js.
     ⚠️ Takes ALREADY-ESCAPED values, matching _viewRow's contract. */
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — `meta` is the "Saved by RW · 8/14/2026" line,
     which used to hang below the whole block in its own italic 11px class. Ryan: *"the 'saved by
     RW – 8/14/2026' line can be moved under the Tracking URL line, and in the same UI format as
     that text too, instead of italicized on an island by itself."*

     ⛔ IT IS A ROW OF THE SUMMARY, NOT A CAPTION UNDER IT. Passing it through `_viewRow` is what
     makes that true rather than approximately true — same label column, same value typography,
     same left edge, automatically and for both cards at once.
     ⚠️ NOT EDITABLE: every other row opens the input form on click, and there is nothing to type
     here — the stamp is written by whoever saved. `_viewRow`'s role="button"/tabindex would
     advertise an affordance that does nothing, so this row is emitted directly.
     ⚠️ Optional, so the lead card and any caller that has no stamp yet are unchanged. */
  function buildSampleTrackingView(carrier, number, url, meta) {
    var rows = _viewRow('Carrier', 'carrier', carrier, 'Add carrier', false)
      + _viewRow('Tracking #', 'number', number, 'Add tracking number', false)
      + _viewRow('Tracking URL', 'url', url, 'Add tracking URL', true);
    if (meta) {
      rows += '<div class="rqd-sample-tracking__k">Saved</div>'
        + '<div class="rqd-sample-tracking__v rqd-sample-tracking__v--static">'
        + '<span class="rqd-sample-tracking__text">' + meta + '</span></div>';
    }
    return rows;
  }

  /* ============================================
     samplesListFor - WHICH FIELD HOLDS THE LIST
     ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30)

     ⛔ THE WRITER IS LEAD-FIRST AND THE READERS WERE `isLead`-GATED, AND THOSE ARE NOT THE SAME
     QUESTION. The picker saves through the worker's `resolveSampleRecord`, whose record order is
     lead → workstation_meta → pending — so a deal that HAS a lead record keeps its samples on the
     lead no matter what status it has since reached. Both readers asked `q.isLead` instead, which
     is false the moment a sample lead becomes a quote.

     Ryan, on RW-1309-A: *"how come on RW-1309-A was there samples sent out, but the top section
     which should list what we sent just says 'None yet – use Adjust to build the list'?"*
     Measured: `lead:RW-1309-A.samples` holds both ECORE items and `lead.status` is `quoted`;
     `workstation_meta:RW-1309-A` has a `sampleTracking` and NO `samples`. So `q.leadSamples` was
     populated, `q.quoteSamples` was empty, and the card read the empty one.

     ⛔ BOTH READERS HAD A COMMENT DESCRIBING THIS EXACT BUG, sitting directly above the wrong
     predicate — dashboard-sample-commission.js ("would show 'None yet' on a deal whose list is
     sitting on its lead record") and dashboard-order-cards.js ("would collapse exactly the deals
     that have a list"). The diagnosis was right both times and `q.isLead` was the wrong way to ask
     it. That is why this is now ONE function with TWO callers rather than a third correct copy.

     ⚠️ It answers "which list", not "which card draws it" — that is leadSamplesBlockRenders below,
     and the two are deliberately separate: ownership must not move when somebody saves. */
  function samplesListFor(q) {
    if (!q) return [];
    var lead = Array.isArray(q.leadSamples) ? q.leadSamples : [];
    if (lead.length) return lead;
    return Array.isArray(q.quoteSamples) ? q.quoteSamples : [];
  }

  /* The request stamp lives beside the list, on the same record, written by `_recordRequest` —
     so it is resolved the same way. Reading the other one labels the button "Request" on a deal
     ECORE has already been asked about. */
  function sampleRequestFor(q) {
    if (!q) return null;
    var lead = Array.isArray(q.leadSamples) ? q.leadSamples : [];
    if (lead.length) return q.sampleRequest || null;
    return q.quoteSampleRequest || q.sampleRequest || null;
  }

  /* ============================================
     sampleSiblingsHtml - THE OTHER DEALS THIS CUSTOMER HAS A BOX ON
     ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-10) — PHASE 3.

     Since Phase 2 a repeat submission from the same customer auto-groups with the deal its owner
     already holds, and the board draws the whole group as ONE cascade. The samples card did not
     know: one customer's two sample jobs each counted their own boxes, and neither said the other
     existed. The worker's `applyGroupSamplesReadThrough` puts a read-only summary of each grouped
     sibling's box on `q.sampleSiblings`; this renders it.

     ⛔ IT SHOWS, IT NEVER INHERITS, AND THAT DISTINCTION IS THE WHOLE SAFETY OF IT. `samplesOriginRef`
     — the revision-chain read-through — is not a display hint: since 2026-09-06 every sample control
     on an inheriting card WRITES to the origin ref (Adjust, Request, the tracking form, Notify, the
     ladder's Stop/Resume). That is right down a revision chain, where there is one deal and one box.
     Across a GROUP it would aim "Request samples" at a different deal's record, under that deal's
     ECORE Project Name, for what may be an entirely different job. So this is prose and a ref, with
     no controls: the sibling's card is where that box is worked.

     ⚠️ ONE HELPER, TWO CARDS — the lead-side block and dashboard-sample-commission.js, for the same
     reason `samplesListFor` and `leadSamplesBlockRenders` are shared. A second copy is how the two
     cards start describing one box differently.
     ⚠️ `state` IS THE WORKER'S WORD. "delivered" needs the FedEx sweep's own evidence
     (`delivery.status === 'DL'` / `deliveredAt`); a tracking number alone only says we posted it,
     and the card must never upgrade that on its own. */
  function sampleSiblingsHtml(q) {
    var sibs = (q && Array.isArray(q.sampleSiblings)) ? q.sampleSiblings : [];
    if (!sibs.length) return '';
    /* ⚠️ ITS OWN ESCAPER, SHADOWING THE MODULE ONE ON PURPOSE. The file-level `esc` is a var
       injected by RQD.initLeadDetail, and this is the first helper exported from here that renders
       MARKUP: the quote-side card calls it from a different IIFE with no load-order guarantee
       against that init, and an undefined `esc` would throw inside a detail render rather than
       quietly drawing nothing. Nothing else in this function depends on the injected pair. */
    var esc = function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
      });
    };
    var lines = sibs.map(function (s) {
      if (!s || !s.ref) return '';
      var n = Number(s.count) || 0;
      var what = n ? (n + ' sample' + (n === 1 ? '' : 's')) : 'a sample box';
      var trk = s.tracking && s.tracking.number
        ? ' · ' + esc(String(s.tracking.carrier || 'tracking')) + ' ' + esc(String(s.tracking.number))
        : '';
      var state = s.state === 'delivered' ? ' · delivered'
        : s.state === 'shipped' ? ' · in transit'
          : s.state === 'requested' ? ' · requested from ECORE, no tracking yet'
            : ' · not requested yet';
      return '<div>' + esc(what) + ' on <strong>' + esc(String(s.ref)) + '</strong>' + trk + state + '</div>';
    }).filter(Boolean).join('');
    if (!lines) return '';
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — THE HAND-MERGE. An online sample request that
       was joined to this customer's earlier deal but not folded (filed before intake did it by
       itself, or grouped by hand) can be folded from here: its titles move onto the sibling's list
       as the next ask and this card becomes that deal's card. Admins only; the worker refuses
       anything that is not an unrequested online sample lead (POST /sample-fold). */
    var fold = '';
    if (getIsAdmin() && String(q.leadCaptureSource || '') === 'sample-request' && !q.samplesFoldedTo && !q.samplesOriginRef) {
      var _req = (typeof sampleRequestFor === 'function') ? sampleRequestFor(q) : (q.sampleRequest || null);
      var _target = (!(_req && _req.sentAt)) ? sibs.find(function (s) { return s && s.ref && s.state !== 'listed'; }) : null;
      if (_target) {
        /* Ryan 2026-09-20: right-aligned in Adjust's column, Adjust's own 112x28 box and colour,
           the label wrapped "Fold into" over the ref at a smaller size so nothing falls off the
           button. The row carries the actions row's inset and scale var; the two spans are the two
           lines (the box is a column flex, see the CSS).
           Ryan 2026-09-24: *"a little too low"* - it used to be its own row UNDER the sibling
           lines. Now it sits BESIDE them, top-right, directly under the dividing line: the text
           is wrapped in `__text` and the block becomes a two-column flex (see `.rqd-sample-siblings--fold`). */
        fold = '<div class="rqd-sample-fold__row"><button type="button" class="rqd-sample-fold rqd-invoice__link" data-ref="' + esc(String(q.reference)) + '" data-into="' + esc(String(_target.ref)) + '"'
          + ' title="Move this request\'s samples onto ' + esc(String(_target.ref)) + ' as its next ask, so one request to ECORE covers the customer\'s box.">'
          + '<span>Fold into</span><span>' + esc(String(_target.ref)) + '</span></button></div>';
      }
    }
    return '<div class="rqd-sample-tracking__meta rqd-sample-siblings' + (fold ? ' rqd-sample-siblings--fold' : '') + '">'
      + '<div class="rqd-sample-siblings__text"><div>Also for this customer, on a grouped deal:</div>' + lines + '</div>' + fold + '</div>';
  }
  window.RQD.sampleSiblingsHtml = sampleSiblingsHtml;

  /* ============================================
     CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-20) — A SECOND BOX ON THE SAME DEAL
     ============================================
     Ryan: *"the samples list showed what we've already sent him, with a label next to it"*, *"the
     list of samples on the card needs to show tags for first shipment and second and on mouseover
     they need to show you tracking numbers"*, *"sub headers for 'SHIPMENT 1' and 'SHIPMENT 2'"*.

     ⛔ THE FIRST-SHIPMENT CARD RENDERS BYTE-FOR-BYTE. Every renderer here falls back to today's
     markup unless the deal has a second ask, a second box or an unrequested addition — Ryan 09-08:
     *"be really careful you don't mess up any of the UI we've carefully created for the first
     sample shipment."*

     ⚠️ THE SIX FIELDS ARE BAKED (worker lib/lead-fields.js + the quote-side builders). A row baked
     before the deploy has NO such keys: `_secondBoxView` reports `known: false` and everything
     renders as it did (memory: baked-row-absent-key-vs-null). A present empty array is the
     worker's honest answer and is trusted. */
  function _secondBoxView(q) {
    if (!q) return { known: false, tags: [], boxes: [], reqCount: 0, newCount: null };
    var lead = Array.isArray(q.leadSamples) && q.leadSamples.length > 0;
    var pick = function (leadKey, quoteKey) {
      if (lead && Object.prototype.hasOwnProperty.call(q, leadKey)) return q[leadKey];
      if (Object.prototype.hasOwnProperty.call(q, quoteKey)) return q[quoteKey];
      if (Object.prototype.hasOwnProperty.call(q, leadKey)) return q[leadKey];
      return undefined;
    };
    var tags = pick('sampleItemTags', 'quoteSampleItemTags');
    var boxes = pick('sampleShipments', 'quoteSampleShipments');
    var reqCount = pick('sampleRequestCount', 'quoteSampleRequestCount');
    var newCount = pick('sampleNewCount', 'quoteSampleNewCount');
    return {
      known: Array.isArray(tags),
      tags: Array.isArray(tags) ? tags : [],
      boxes: Array.isArray(boxes) ? boxes : [],
      reqCount: Number(reqCount) || 0,
      newCount: (typeof newCount === 'number') ? newCount : null,
    };
  }
  window.RQD.secondBoxView = _secondBoxView;

  /** Titles on the list no ask has named. Bridges a stale bake from the first ask's own titles. */
  function sampleNewCountFor(q) {
    var v = _secondBoxView(q);
    var list = samplesListFor(q);
    if (v.known) {
      /* The row may be a step behind the list (a picker save repaints the DOM before the refetch):
         a title the tags do not know is new by definition. */
      var seen = {};
      v.tags.forEach(function (t) { if (t && t.name) seen[String(t.name).trim().toLowerCase()] = 1; });
      var unseen = list.filter(function (s) { return s && !seen[String(s).trim().toLowerCase()]; }).length;
      return Math.max(v.newCount || 0, unseen);
    }
    var req = (typeof sampleRequestFor === 'function') ? sampleRequestFor(q) : (q && q.sampleRequest);
    var named = {};
    ((req && Array.isArray(req.items)) ? req.items : []).forEach(function (t) { named[String(t).trim().toLowerCase()] = 1; });
    ((req && Array.isArray(req.additional)) ? req.additional : []).forEach(function (a) {
      ((a && Array.isArray(a.items)) ? a.items : []).forEach(function (t) { named[String(t).trim().toLowerCase()] = 1; });
    });
    if (!req || !req.sentAt || !(req.items && req.items.length)) return 0;
    return list.filter(function (s) { return s && !named[String(s).trim().toLowerCase()]; }).length;
  }
  window.RQD.sampleNewCountFor = sampleNewCountFor;

  /** Does this deal render the second-box view at all? */
  function sampleSecondBoxActive(q, list) {
    var v = _secondBoxView(q);
    if (!v.known || v.reqCount < 1) return false;
    if (v.reqCount > 1 || v.boxes.length > 1) return true;
    return sampleNewCountFor(q) > 0;
  }
  window.RQD.sampleSecondBoxActive = sampleSecondBoxActive;

  function _boxTagAttrs(box, stateText, tone) {
    var url = String(box.url || '');
    var label = (box.carrier ? String(box.carrier) + ' ' : '') + String(box.number || '');
    return ' tabindex="0"'
      + ' data-track-url="' + esc(url) + '"'
      + ' data-track-label="' + esc(label) + '"'
      + ' data-track-saved="' + esc(box.savedAt ? (formatDate(box.savedAt) || '') : '') + '"'
      + ' data-track-state="' + esc(stateText) + '"'
      + ' data-track-tone="' + esc(tone || '') + '"';
  }

  /** The tag beside one sample. `t` is a worker `sampleItemTags` row, or null for a title it does not know. */
  function sampleTagHtml(t) {
    if (!t || !t.requestNo) {
      return '<span class="rqd-sample-tag rqd-sample-tag--new" title="On the list, not yet requested from ECORE. Request more sends it.">New</span>';
    }
    var box = t.box || null;
    if (box && t.state === 'delivered') {
      var d = box.deliveredAt ? (formatDate(box.deliveredAt) || '') : '';
      var s1 = 'Delivered' + (d ? ' ' + d : '');
      return '<span class="rqd-sample-tag rqd-sample-tag--delivered"' + _boxTagAttrs(box, s1, 'ok') + ' title="Shipment ' + box.seq + ' · ' + esc(s1) + ' — hover for tracking">Shipment ' + box.seq + ' · delivered</span>';
    }
    if (box) {
      var s2 = 'In transit' + (box.savedAt ? ' · shipped ' + (formatDate(box.savedAt) || '') : '');
      return '<span class="rqd-sample-tag rqd-sample-tag--shipped"' + _boxTagAttrs(box, s2, '') + ' title="Shipment ' + box.seq + ' · ' + esc(s2) + ' — hover for tracking">Shipment ' + box.seq + ' · in transit</span>';
    }
    var w = t.requestedAt ? (formatDate(t.requestedAt) || '') : '';
    return '<span class="rqd-sample-tag rqd-sample-tag--requested" title="Asked ECORE' + (w ? ' on ' + esc(w) : '') + '. No tracking number yet.">Requested' + (w ? ' ' + esc(w) : '') + ' · awaiting box</span>';
  }
  window.RQD.sampleTagHtml = sampleTagHtml;

  /** THE list renderer — both cards and the picker's repaint. `samples` defaults to the row's list. */
  function sampleListHtml(q, samples, opts) {
    var list = Array.isArray(samples) ? samples : samplesListFor(q);
    list = list.filter(function (s) { return s && String(s).trim(); });
    if (!list.length) return '<em style="color:var(--rq-text-secondary);">None yet</em>';
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: its list is plain lines; the
       request/box tags describe the BUYER's chain and must not be painted onto the other person's set. */
    if ((opts && opts.lane === 2) || !sampleSecondBoxActive(q, list)) {
      return list.map(function (s) { return esc(String(s).trim()); }).join('<br>');
    }
    var byName = {};
    _secondBoxView(q).tags.forEach(function (t) { if (t && t.name) byName[String(t.name).trim().toLowerCase()] = t; });
    return list.map(function (s) {
      var name = String(s).trim();
      return '<div class="rqd-sample-item"><span class="rqd-sample-item__name">' + esc(name) + '</span>' + sampleTagHtml(byName[name.toLowerCase()] || null) + '</div>';
    }).join('');
  }
  window.RQD.sampleListHtml = sampleListHtml;

  /** Earlier boxes, read-only, and the "Shipment N" caption for the live widget. '' with one box. */
  function priorShipmentsHtml(q) {
    var boxes = _secondBoxView(q).boxes;
    if (boxes.length < 2) return '';
    var latest = boxes[boxes.length - 1];
    var html = '';
    boxes.slice(0, -1).forEach(function (b) {
      var d = b.deliveredAt ? (formatDate(b.deliveredAt) || '') : '';
      var link = b.url
        ? '<a href="' + esc(String(b.url)) + '" target="_blank" rel="noopener">' + esc(String(b.number || '')) + '</a>'
        : esc(String(b.number || ''));
      html += '<div class="rqd-sample-prior">'
        + '<div class="rqd-detail__section-title rqd-detail__section-title--sub rqd-sample-prior__title">Shipment ' + b.seq + '</div>'
        + '<div class="rqd-sample-prior__row">'
        +   (b.carrier ? esc(String(b.carrier)) + ' ' : '') + link
        +   (b.savedAt ? ' · shipped ' + esc(formatDate(b.savedAt) || '') : '')
        +   (d ? ' · <strong>delivered ' + esc(d) + '</strong>' : (b.trouble ? ' · <strong>carrier problem</strong>' : ' · in transit'))
        + '</div>'
        + '</div>';
    });
    html += '<div class="rqd-detail__section-title rqd-detail__section-title--sub rqd-sample-prior__title">Shipment ' + latest.seq + ' <span class="rqd-sample-prior__latest">latest</span></div>';
    return html;
  }
  window.RQD.priorShipmentsHtml = priorShipmentsHtml;

  /** After a picker save: the Request button's label follows the list without a refetch. */
  function paintSampleRequestButtons(ref, q) {
    if (!q) return;
    var req = (typeof sampleRequestFor === 'function') ? sampleRequestFor(q) : (q.sampleRequest || null);
    if (!(req && req.sentAt)) return;
    var n = sampleNewCountFor(q);
    document.querySelectorAll('.rqd-sample-request[data-ref="' + (window.CSS && CSS.escape ? CSS.escape(ref) : ref) + '"]:not([data-lane="2"])').forEach(function (b) { // CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient's button is its own
      b.textContent = n > 0 ? 'Request more' : 'Sent';
      b.classList.toggle('rqd-sample-request--sent', !(n > 0));
      b.classList.toggle('rqd-sample-request--more', n > 0);
      if (n > 0) b.title = n + ' new sample' + (n === 1 ? '' : 's') + ' on the list not yet requested. Opens a reply on the original request asking ECORE to ship them.';
    });
  }
  window.RQD.paintSampleRequestButtons = paintSampleRequestButtons;
  /* END CUSTOM_SAMPLES2ND_BY_CLAUDE */

  /* ============================================
     leadSamplesBlockRenders - WHICH CARD OWNS THE SAMPLES BLOCK
     ============================================
     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25)

     ⛔ ONE PREDICATE, TWO READERS. The lead-side block and the quote-level one
     (dashboard-sample-commission.js) render the SAME markup with the SAME classes -
     `.rqd-lead-samples[data-ref]` is the single selector dashboard-sample-picker.js repaints
     by, and `.rqd-sample-adjust` is the single delegated click it listens for. If both cards
     ever draw for one ref, a save repaints whichever is first in the DOM and leaves the other
     showing a stale list, with nothing on screen saying which is which.

     So the rule lives HERE and the quote card asks rather than re-deriving: a SAMPLE-REQUEST
     deal's samples belong to the lead card, because that is where the customer's own
     selections live and where the resolver writes them. Every other deal's belong to the
     quote card. A second copy of this expression is how the two cards start disagreeing
     about who is drawing.

     ⚠️ DELIBERATELY NOT A TEST ON THE LIST'S LENGTH. Ownership must not change when somebody
     saves - see the note at the call site. */
  function leadSamplesBlockRenders(q) {
    if (!q) return false;
    var list = Array.isArray(q.leadSamples)
      ? q.leadSamples.filter(function (s) { return s && String(s).trim(); })
      : [];
    /* CUSTOM_PHONESAMPLES_BY_CLAUDE (Ryan 2026-10-01, RW-1451-A) — ⛔ THE CARD THAT DRAWS THE
       TRACKING DRAWS THE LIST. A phone / email / CFA lead that had samples sent is not a
       sample-request deal, so the list was left to the quote-level card — which hides itself the
       moment the LEAD carries a tracking number (ECORE's auto-recorded tracking lands there,
       lead-first), to avoid two tracking blocks. The list fell between the two cards: Ryan could
       see the box and the follow-ups but not what was in it. Same handoff point the quote card
       already uses, so the two still never both draw the list. */
    if (String(q.leadCaptureSource || '') !== 'sample-request') {
      return leadTrackingBlockRenders(q) && (list.length > 0 || getIsAdmin());
    }
    /* A sample deal with an EMPTY list still gets the block, for admins - PM14's rule: the deal
       that most needs a list was the one with nowhere to put the Adjust button. */
    return list.length > 0 || getIsAdmin();
  }
  window.RQD.leadSamplesBlockRenders = leadSamplesBlockRenders;
  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — exported for the quote-level Samples card AND
     for shouldAutoCollapse, the two places that were each carrying their own `q.isLead ? … : …`.
     ⚠️ Both callers guard on `typeof === 'function'` and fall back, because dashboard-order-cards.js
     has no load-order guarantee against this file and a collapse rule that throws would take the
     whole detail render with it. */
  window.RQD.samplesListFor          = samplesListFor;
  window.RQD.sampleRequestFor        = sampleRequestFor;

  /* ⛔ THE SAME QUESTION FOR THE TRACKING + LADDER HALF OF THIS CARD, and it needs its own
     answer because the two halves do not turn on together: a promoted sample deal can have a
     samples LIST here and its tracking on the quote record, or the reverse.
     ⚠️ EXPORTED FOR THE `sampleNotifiedAt` LEG SPECIFICALLY. The quote-level card already
     suppresses itself on `isSample` and on a lead tracking number, so those two can never
     collide - but a deal that was NOTIFIED and has since had its tracking cleared satisfies
     neither, and both cards drew. That was survivable while this block was the only one with
     an off switch in it; it stopped being survivable the day the quote card grew the same two
     Stop buttons, because two Stop buttons for one automation is a control whose state one
     copy of it does not know about. */
  function leadTrackingBlockRenders(q) {
    if (!q) return false;
    return !!(q.isSample || (q.sampleTracking && q.sampleTracking.number) || q.sampleNotifiedAt);
  }
  window.RQD.leadTrackingBlockRenders = leadTrackingBlockRenders;

  window.RQD.buildLeadDetail = buildLeadDetail;
  window.RQD.initLeadDetail = initLeadDetail;
  window.RQD.paintSampleTrackingView = paintSampleTrackingView;
  window.RQD.buildSampleTrackingView = buildSampleTrackingView;
})();
